
const sharedVoiceReasons = {
  "not_granted": "Your account does not have voice access.",
  "disabled": "Voice mode is disabled.",
  "spend_limit": "The monthly spend limit has been reached.",
  "provider_error": "The voice service failed. Please try again.",
};

export const en = {
  // Browser mirror of src/push/messages.ts; tests/contract/pushCopy.test.ts pins the server copy.
  pushFailure: {"title":"Conversation failed","provider":"AI provider error","chetty":"Chetty error"},
  totp: {
    qrTitle: "QR code",
    title: "Two-step verification",
    description: "Protect password sign-in with a six-digit code from your authenticator. Microsoft sign-in follows your organization’s policy.",
    code: "Authenticator code",
    codeValidFor: "The code is valid for another {seconds} s",
    codeSecondsLeft: "{seconds} s",
    verify: "Verify",
    loginTitle: "Verify your sign-in",
    lost: "Lost your authenticator? Contact another administrator. There are no recovery codes or password bypass.",
    invalid: "The code could not be verified. Wait for a new code or restart sign-in.",
    expired: "Verification expired. Start again.",
    enable: "Enable two-step verification",
    confirm: "Confirm and enable",
    disable: "Disable two-step verification",
    disabled: "Not enabled",
    enabled: "Enabled",
    enabledAt: "Enabled on {date}",
    trusted: "This session is trusted until {date}.",
    untrusted: "This session has no recorded second-factor verification.",
    trustWarning: "Trust lasts for the existing session lifetime, not for a separate device setting. Anyone with this browser’s session cookie can access your account until expiry or revocation.",
    adminWarning: "Before enabling: arrange another administrator or usable Microsoft sign-in. A lone administrator who loses the authenticator has no recovery. There are no recovery codes, hidden resets or CLI recovery.",
    password: "Current password",
    passwordRefused: "The current password could not be verified.",
    setup: "Scan the QR code in your authenticator, or enter the setup key manually.",
    manualKey: "Setup key",
    copyKey: "Copy setup key",
    copied: "Setup key copied",
    qrAlt: "Authenticator setup QR code",
    qrFailed: "The QR code could not be generated. Enter the setup key manually.",
    expires: "Setup expires at {date}.",
    pending: "An unfinished setup exists, but its key cannot be shown again. Cancel it and start again.",
    cancelSetup: "Cancel setup",
    disableWarning: "Use a new, unused code. Disabling signs out every browser session, including this one. API tokens and paired devices are unchanged.",
    loadError: "Security information could not be loaded.",
    actionError: "The action could not be completed. Check the current status before retrying.",
    cleanupUnverified: "The change was saved, but closing the sessions could not be verified.",
    stale: "Security settings changed or this setup expired. Reload the current status.",
    sessions: "Signed-in sessions",
    sessionsHint: "The name comes from browser information at sign-in. Revoking a session briefly reconnects this account’s other browser connections.",
    unknownDevice: "Unknown device",
    current: "Current session",
    started: "Started {date}",
    expiresSession: "Expires {date}",
    methodPassword: "Password",
    methodTotp: "Password and authenticator",
    methodSso: "Microsoft",
    revoke: "Revoke session",
    revokeTitle: "Revoke this session?",
    revokeBody: "This session will be signed out. The account’s other browser connections briefly reconnect. API tokens and paired devices are unchanged.",
    emptySessions: "No login sessions.",
    loadMore: "Load more",
    reset: "Reset two-step verification",
    resetTitle: "Reset verification for {name}?",
    resetBody: "Verify the person independently first. Reset disables their factor and signs out all their browser sessions. They must enroll again. This is not self-service recovery.",
    proofPassword: "Use my password",
    proofSso: "Use my recent Microsoft sign-in",
    proofSsoHint: "Requires a Microsoft sign-in within the last five minutes. Otherwise sign out and sign in with Microsoft again.",
    resetSuccess: "Two-step verification reset.",
    resetError: "Reset was refused or could not be confirmed. Reload the account before retrying.",
    rateRetry: "Too many attempts. Try again in {seconds} seconds.",
  },
  connectionStatus: {
    computerOffline: 'Computer {computer} is offline; work is preserved',
    unknown: 'Connection state unknown',
    connected: 'Connected',
    connecting: 'Connecting',
    disconnected: 'Disconnected',
    pending: 'Waiting for readiness',
    stale: 'Last known state; current state unknown',
    freshnessUnknown: 'Observation freshness unknown',
    transport: 'Connection',
    model: 'Model request',
    waitingForComputer: 'Waiting for the project computer; work is preserved',
    remoteRetryUnknown: 'Remote retry progress unknown',
  },
  connectionFailure: {
    startFailed: 'Conversation could not be opened',
    policyUnavailable: 'Connection retry settings are unavailable',
    streamFailed: 'Connection failed',
  },
  /** Labels of the closed menu-action vocabulary (`components/ui/menuActions.ts`). */
  menuActions: {
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    replace: 'Replace',
    rename: 'Rename',
    duplicate: 'Duplicate',
    copy: 'Copy',
    enable: 'Turn on',
    disable: 'Turn off',
    archive: 'Archive',
    restore: 'Restore',
    reset: 'Reset to default',
    open: 'Open',
    view: 'View details',
    move: 'Move',
    pin: 'Pin',
    unpin: 'Unpin',
    mute: 'Mute',
    unmute: 'Unmute',
    download: 'Download',
    select: 'Select',
    add: 'Add',
    settings: 'Settings',
    update: 'Update',
    invite: 'Invite',
    leave: 'Leave',
    connect: 'Connect',
    disconnect: 'Disconnect',
  },
  conversationIcon: { title: 'Conversation icon', change: 'Change icon: {title}' },
  identityIcons: {
    Briefcase: 'Briefcase',
    Server: 'Server',
    Database: 'Database',
    Heart: 'Heart',
    Code: 'Code',
    Home: 'Home',
    Star: 'Star',
    Folder: 'Folder',
    Globe: 'Globe',
    Book: 'Book',
    Cpu: 'Processor',
    Terminal: 'Terminal',
    Rocket: 'Rocket',
    Lightbulb: 'Lightbulb',
    Target: 'Target',
    Calendar: 'Calendar',
    DollarSign: 'Dollar',
    ShoppingCart: 'Shopping cart',
    Music: 'Music',
    Camera: 'Camera',
    MapPin: 'Map pin',
    Zap: 'Lightning',
    Flag: 'Flag',
    Bookmark: 'Bookmark',
    MessageCircle: 'Chat bubble',
  },
  voice: {
    "settingsSection": "Voice mode",
    "accountSection": "Voice",
    "settingsHint": "Provider, models and call duration.",
    "accountHint": "Your voice, language and callbacks.",
    "settings": {
      "title": "Voice mode",
      "enabled": "Enabled",
      "enabledHint": "Calls and voice notes use the instance voice provider. Every account also needs the voice grant.",
      "provider": "Provider",
      "providerHint": "OpenAI or Azure OpenAI providers with an API key are eligible. Azure requires an .../openai/v1 endpoint and a Global Standard deployment named exactly like each model.",
      "chooseProvider": "Choose a provider",
      "callModel": "Call model",
      "callModelHint": "GPT-Live 1 calls cost $0.05 per minute, billed per second, including time spent waiting. Work done for the conversation is billed separately.",
      "transcriptionModel": "Transcription model",
      "dictationModel": "Dictation model",
      "dictationModelHint": "Writes your speech into the message field while you talk. It is billed by the minute of audio.",
      "speechModel": "Speech model",
      "maxCallSeconds": "Maximum call length",
      "ringSeconds": "Ring time",
      "seconds": "{n} s",
      "holdSeconds": "Holding the line",
      "holdSecondsHint": "How long Chetty waits on the line for a task result. Waiting is part of the paid call. After this limit, it says goodbye and calls back when done if callbacks are enabled in your account. At 0 it says goodbye right away.",
      "holdSecondsNever": "No holding",
      "contextMessages": "Messages Chetty knows on a call",
      "contextMessagesHint": "How many recent written messages Chetty reads before a call. Tool results are excluded. More messages give better context without changing the call rate.",
      "noProvider": "No eligible provider",
      "noProviderHint": "Add an OpenAI or Azure OpenAI provider with an API key in AI settings. Azure requires an .../openai/v1 endpoint and a Global Standard deployment named exactly like each model.",
      "chooseModels": "Choose an eligible provider and offered models before enabling voice.",
      "loadError": "Could not load voice settings."
    },
    "account": {
      "title": "Voice",
      "voice": "Voice",
      "voiceHint": "The voice Chetty uses on calls. Play a sample with the speaker icon.",
      "playSample": "Play the {voice} voice sample",
      "stopSample": "Stop the {voice} voice sample",
      "presentation": {
        "feminine": "Female",
        "masculine": "Male"
      },
      "accent": {
        "australian": "Australian English",
        "british": "British English",
        "irish": "Irish English",
        "northAmerican": "American English",
        "brazilian": "Brazilian Portuguese",
        "filipino": "Filipino English",
        "southernUs": "Southern US English"
      },
      "language": "Language",
      "languageHint": "Account language uses your account locale. You can enter another language code.",
      "accountLanguage": "Account language",
      "czech": "Czech",
      "slovak": "Slovak",
      "english": "English",
      "customLanguage": "Other language",
      "languageCode": "Language code",
      "callback": "Call me back",
      "callbackHint": "After a voice brief, Chetty can call you back when it needs an answer or has the result.",
      "dictationPause": "Stop dictation after a pause",
      "dictationPauseHint": "When you stay quiet this long, dictation ends by itself and the text stays in the field. You can send it at any time, even while dictating.",
      "dictationPauseNever": "Never",
      "unavailable": "Voice is unavailable",
      "unavailableHint": "An administrator must configure an eligible provider and supported models. You can still change language and callback preferences.",
      "loadError": "Could not load voice preferences."
    },
    "permission": {
      "title": "Voice access",
      "allow": "Allow voice",
      "hint": "Allows calls, callbacks and voice notes. Administrators need this grant too."
    },
    operations: {
      "kind": "Operation",
      "reason": "Reason",
      "status": {
        "submitted": "Submitted to conversation",
        "started": "Started",
        "succeeded": "Succeeded",
        "failed": "Failed",
        "cancelled": "Cancelled"
      },
      "kinds": {
        "interpretation": "Request interpretation",
        "work": "Work",
        "answers": "Answers",
        "clarify": "Clarification",
        "end": "End call",
        "callback": "Callback"
      },
      "reasons": {
        "policy_refusal": "Refused under the provider’s usage policy",
        "refusal": "Request refused",
        "quota": "Quota exhausted",
        "rate_limit": "Rate limit reached",
        "unavailable": "Service unavailable",
        "timeout": "Timed out",
        "tool_error": "Tool failed",
        "invalid_response": "Invalid response",
        "cancelled": "Cancelled",
        "unknown": "Unknown error"
      }
    },
    "transcript": {
      "title": "Call transcript",
      "call": "Call",
      "user": "You",
      "assistant": "Chetty",
      "empty": "No transcript available"
    },
    actionsGroup: "Voice",
    dictation: {
      "start": "Dictate",
      "hint": "Type into the message field by voice",
      "stop": "Done",
      "cancel": "Cancel dictation",
      "recording": "Recording",
      "transcribing": "Transcribing…",
      "errors": {
        ...sharedVoiceReasons,
        "mic_denied": "Microphone access was denied. Allow it and try again.",
        "dictation_active": "Dictation is already active for your account. End it in the other tab before starting again.",
        "mic_failed": "The microphone could not be used.",
        "network": "Dictation lost its connection. Check your connection and try again.",
      }
    },
    call: {
      "start": "Call",
      "hint": "Give a task by voice and hear the result",
      "hangup": "Hang up",
      "close": "Close",
      "states": {
        "connecting": "Connecting…",
        "listening": "Listening",
        "speaking": "Speaking",
        "on_hold": "Chetty is working",
        "ending": "Ending call…"
      },
      "reasons": {
        ...sharedVoiceReasons,
        "unknown_session": "The conversation no longer exists.",
        "call_active": "Another voice call is already in progress.",
        "callback_unavailable": "This callback is no longer available.",
        "question_not_pending": "The question no longer needs an answer.",
        "voice_unavailable": "The voice service is unavailable.",
        "provider_unavailable": "No eligible provider or supported priced model is available.",
        "mic_denied": "Microphone access was denied. Please allow it and try again.",
        "disconnected": "The call was disconnected.",
        "daemon_restart": "The service restarted.",
        "hangup": "Call ended.",
        "completed": "Your brief was submitted.",
        "max_duration": "The maximum call length was reached.",
        "silence": "The call ended after inactivity."
      }
    },
    ring: {
      "acceptUnavailable": "Voice calls are not available right now.",
      "title": "Incoming call",
      "doneTitle": "Chetty has the result",
      "accept": "Accept",
      "decline": "Decline",
      "close": "Close",
      "loading": "Checking call…",
      "stale": "This call is no longer available.",
      "error": "The call could not be checked. Please try again.",
      "declineError": "The call could not be declined. Please try again.",
      "timeout": "The call has expired.",
      "declined": "Call declined.",
      "accepted": "The call was accepted.",
      "unavailable": "This account can no longer take this call.",
      "settled": "The question has already been answered.",
      "restart": "The service restarted."
    },
  },
  spendLimit: {
    section: "Limit and spend",
    unlimited: "Unlimited",
    limit: "Monthly limit",
    spent: "Spent",
    reset: "Resets at",
    resetUsage: "Reset limit",
    limitHint: "Once this amount is reached, the account starts no further work until the limit resets.",
    resetHint: "When the monthly limit starts over.",
    resetUsageHint: "This month's spend counts toward the limit from zero again; statistics and conversations are kept.",
    spentHint: "How much this account has spent this month.",
    resetTitle: "Reset the monthly limit?",
    resetDescription: "For the rest of this month, the monthly limit of {user} counts spend from zero. Statistics, provider account spend and conversations remain unchanged.",
    resetError: "The monthly limit could not be reset.",
    readError: "Could not load account spending.",
    monthlyLimit: "The monthly spend limit has been reached. Resets at {date}.",
    unpricedModel: "The model has no known price and cannot be used by an account with a spending limit. The monthly limit resets at {date}.",
    accountLimit: "Account {account} is held by its monthly spend limit. It resets at {date}.",
  },
  updateStatus: {
    prepareCoreTitle: "Prepare a joint update?",
    prepareCoreIntro: "This plugin needs a newer Chetty. Preparation will include the system update and validate plugins using the target version. New permissions will be asked separately; existing approvals stay unchanged.",
    prepareCore: "Prepare system and plugins",
    consentTitle: "Prepared update permissions",
    consentIntro: "Plugins requiring new permissions have not been activated. Other compatible updates may have completed meanwhile. Approval covers only the packages listed here and immediately resumes their verified update.",
    reviewConsent: "Review permissions",
    approveConsent: "Approve and continue",
    declineConsent: "Decline",
    consentError: "Could not save the decision. Reload the status and check the current question.",
    consentChanged: "The package or consent changed. Reload the status. If the package changed, prepare the update again before approving it.",
    consentQueued: "The approved update is queued.",
    consentDeclined: "The update was declined.",
    noNewGrants: "No new permissions",
    notInstalled: "Not installed",
    "updatesHeld": "The publisher has paused new releases for this licence. Previously available versions can still be installed and repaired.",
    "title": "Update status",
    "error": "Could not read update status.",
    "queued": "Queued",
    "downloading": "Downloading package",
    "prepared": "Preparing",
    "quiescing": "Stopping services",
    "applying": "Applying",
    "verifying": "Checking startup",
    "committing": "Finishing",
    "committed": "Completed",
    "rollingBack": "Restoring previous version",
    "blocked": "Blocked before activation",
    "rolledBack": "Rolled back",
    "needsOperator": "Operator attention required",
    "applied": "Applied",
    "needsConsent": "Consent required",
    "notLicensed": "Not licensed",
    "superseded": "Cancelled before execution",
    "failed": "Failed",
    "skipped": "Skipped",
    "noUpdate": "Already up to date",
    "repair": "Repair",
    "repairError": "Could not request the repair.",
    "degraded": "Not loaded",
  },
  problemReporting: {
    delivery: { idle: "Ready", sent: "Delivered", retry: "Retrying", credentials: "Invalid licence", unavailable: "Server unavailable", storage: "Storage error" },
    label: "Problem reporting",
    notice: "Send daemon warnings and errors to the Chetty licence-server operator. Reports contain problem codes, counts, warning/error levels, real names of all plugins, including private and unlicensed plugins, product and plugin versions, and up to three samples of the error message text per problem. Event-loop lag reports also include the latest host sample: CPU, RAM, disk capacity, disk I/O and network rates, and its timestamp. Before anything is stored or sent, the text is cut to 500 characters and keys, tokens, passwords, cookies, URL parameters, e-mail addresses, IP addresses, identifiers and directory paths are removed from it. The remaining text may name providers, models, plugin components, host names or people. Error messages are diagnostics, not conversations or memories, and every report sends the redacted text of the message it describes, including messages that can carry message content or customer business data. Reports identify this licensed instance and are retained for at most 14 days. Turning this off clears unsent batches but cannot recall data already received.",
    confirmTitle: "Enable problem reporting?",
    confirm: "Confirm and enable",
    loadError: "Problem reporting status could not be loaded.",
  },
  guides: {
    count: { one: '{n} guide', few: '{n} guides', many: '{n} guides' },
    enlargeImage: 'Enlarge image: {alt}',
    aiPending: 'Preparing an answer from the guides…',
    "description": "Practical instructions for working with Chetty.",
    "search": "Search guides",
    "clear": "Clear search",
    "categories": "Categories",
    "recommended": "Recommended",
    "empty": "No guides found",
    "emptyDescription": "Try another question or browse the categories.",
    "unavailable": "Guides could not be loaded.",
    "notFound": "This guide or category does not exist.",
    "notTranslated": "Not translated · Available in English",
    "admin": "For administrators",
    "advanced": "Advanced",
    "advancedDescription": "Terminal app, API, MCP, Projects with a terminal and Git, sub-agents and permissions.",
    "adminDescription": "Setting up Chetty, accounts and access, AI models, plugins, channels and operations.",
    "developer": "For developers",
    "developerDescription": "Building plugins: the manifest, host seams and controls, HTTP routes, data, connecting plugins and releases.",
    "everyday": "Everyday guides",
    "moreGuides": "More guides",
    "reviewed": "Reviewed {date}",
    "outline": "On this page",
    "copyLink": "Copy link",
    "previous": "Previous guide",
    "next": "Next guide",
    "related": "Related guides",
    "updates": "Related updates",
    "updatesError": "Related updates could not be loaded.",
    "all": "All guides",
    "results": "{count} guides found",
    "filter": "Category: {category}",
    "aiUnavailable": "AI answers are unavailable. You can still search the guides.",
    "aiDenied": "Your account cannot use the answer model.",
    "aiLimited": "The answer limit has been reached. Try again later.",
    "aiFailed": "The AI answer could not be loaded.",
    "aiEmpty": "The documentation does not contain an answer to this question.",
    apiReference: {
      title: 'Endpoints you can call',
      search: 'Search endpoints',
      clear: 'Clear search',
      count: { one: '{n} endpoint', few: '{n} endpoints', many: '{n} endpoints' },
      results: 'Showing {shown} of {total} matching endpoints',
      unavailable: 'The API catalog could not be loaded.',
      searchError: 'The endpoint search could not be completed.',
      empty: 'No endpoints available',
      emptyDescription: 'Your account cannot call any endpoint on this instance.',
      noResults: 'No matching endpoints',
      noResultsDescription: 'Try other words, or clear the search to browse every endpoint.',
      core: 'Chetty core',
      coreDescription: 'Conversations, Projects, accounts, memory, settings and the rest of the core application.',
      anyMethod: 'Any method',
      detailError: 'The details of this endpoint could not be loaded.',
      detailGone: 'This endpoint is no longer available to your account.',
      params: 'Path parameters',
      query: 'Query parameters',
      body: 'Request body',
      noInput: 'This endpoint takes no input.',
      noFields: 'No fields.',
      openSchema: 'Any JSON is accepted here; the endpoint does not declare its fields.',
      required: 'Required',
      optional: 'Optional',
      truncated: 'The schema is large, so only its top-level fields are shown.',
      adminOnly: 'Administrators only',
      response: { binary: 'File response', stream: 'Streamed response' },
      noApiToken: 'Not available with a personal API token',
      noAgent: 'Not available to the agent',
      coreAdminNote: 'Core endpoints check administrator rights themselves, so this one can still refuse an account that is not an administrator.',
      curlExample: 'curl with a personal API token',
      elowenApiExample: 'ElowenApi tool call',
      copy: 'Copy',
    },
},
  nav: {
    resizeNavigation: 'Resize navigation',
    guides: 'Guides',
    workingConversations: 'Conversations in progress',
    pinnedConversations: 'Pinned conversations',
    hideEntry: 'Hide',
    moveUp: 'Move up',
    moveDown: 'Move down',
    showHidden: 'Show hidden',
    hiddenCount: 'Hidden ({count})',
    // The keys that fold the Studio sidebar. Written out rather than detected per platform: a title that
    // differs between the server render and the client would be a hydration mismatch, and both key names
    // fit in one label.
    collapseShortcut: 'Ctrl / ⌘ + \\',
    // Section headers over the navigation column's two regions, and the labels its new chrome needs.
    sectionWork: 'Work',
    sectionInstance: 'Instance',
    instanceMenu: 'Instance menu',
    home: 'Home',
    chat: 'Chat',
    system: 'System',
    account: 'Account',
    dashboard: 'Dashboard',
    settings: 'Settings',
    projects: 'Projects',
    users: 'Users',
    memory: 'Memory',
  },
  page: {
    guides: 'Guides',
    dashboard: 'Dashboard',
    chat: 'Chat',
    settings: 'Settings',
    projects: 'Projects',
    users: 'Users',
    memory: 'Memory',
  },
  stats: {
    cardTotalCost: 'Tracked cost',
    cardTotalTokens: 'Total tokens',
    cardCache: 'Cached tokens',
    cardModelsUsed: 'Models used',
    emptyTitle: 'No usage yet',
    emptyDesc: 'Token and cost figures appear here once agents have run.',
    modalTitle: 'Usage',
    model: 'Model',
    sectionConversation: 'This conversation',
    sectionModels: 'All models',
    contextLabel: 'Context',
    sessionTokens: 'Session tokens',
    inputTokens: 'Input tokens',
    outputTokens: 'Output tokens',
    cacheHit: 'Share read from cache',
    speed: 'Effective speed',
    arrowHint: '\u2190 \u2192 \u00b7 switch sections',
    sectionContext: 'Context',
    contextWindow: 'Context window',
    contextReported: 'Reported by provider',
    contextBreakdown: 'What is filling it',
    contextEstimateNote: 'The breakdown is an estimate. Each provider counts tokens its own way.',
    contextEmptyTitle: 'Nothing to measure',
    contextEmptyDesc: 'Open a conversation and send a message to see its current context.',
    contextCategory: {
      system: 'System prompt',
      tools: 'Tool schemas',
      user: 'Your messages',
      assistant: 'Replies',
      toolResults: 'Tool output',
      other: 'Other',
    },
    contextCategoryFree: 'Free space',
    compactsAt: 'Context shortens at',
    heaviestTools: 'Tools with the largest output',
    toolColumnsHint: 'Schema \u00b7 calls \u00b7 output.',
  },
  reasoning: {
    modalTitle: 'Reasoning',
    effortLabel: 'Effort',
    thoughtRows: 'Reasoning lines',
    noLevelsTitle: 'No effort levels',
    noLevelsDesc: 'This model does not offer a reasoning effort.',
  },
  helpModal: {
    filterPlaceholder: 'Filter commands',
    shortcutFilterPlaceholder: 'Filter shortcuts',
    shortcutCapturePlaceholder: 'Press keys…',
    emptyTitle: 'No matching command',
    emptyDesc: 'Try a different word, or clear the filter to see every command.',
  },
  tasksModal: {
    statusPending: 'Pending',
    statusInProgress: 'In progress',
    statusCompleted: 'Completed',
    taskActions: 'Task actions',
  },
  aiElements: {
    waterfall: {
      title: 'Trace',
      milliseconds: '{value} ms',
      timing: '{status}, starts at {start} ms, runs {duration} ms',
      status: { running: 'running', completed: 'completed', failed: 'failed' },
    },
    progressLabel: 'Questionnaire progress',
    progress: 'Question {current} of {total}',
    answerRequired: 'Select or enter an answer.',
    previous: 'Previous',
    skip: 'Skip',
    next: 'Next',
    submit: 'Submit',
  },
  common: {
    codeDigit: 'Digit {index} of {count}',
    copyFailed: 'Copy failed',
    copiedToast: 'Copied.',
    actionFailed: 'The action could not be completed.',
    daemonReconnecting: 'Restarting services, please wait.',
    saveUnconfirmed: 'Connection interrupted. Check the change before retrying.',
    savedToast: 'Changes saved.',
    daemon: 'daemon',
    daemonUnreachable: 'Chetty service is unavailable',
    toggleSidebar: 'Toggle menu',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    retry: 'Try again',
    close: 'Close',
    back: 'Back',
    forward: 'Forward',
    scrollLeft: 'Scroll left',
    scrollRight: 'Scroll right',
    saving: 'Saving…',
    saved: 'Saved',
    activationPending: 'Saved; activation pending',
    saveFailed: 'Couldn\'t save',
    saveConflict: 'These settings changed elsewhere',
    reloadChanges: 'Reload current',
    keepMyChanges: 'Keep my changes',
    done: 'Done',
    searchCommands: 'Search commands…',
    searchSite: 'Search…',
    searchNoResults: 'No results',
    /** Heading of the palette's second-pass group: rows nothing matched literally, found by meaning
     *  (embedding similarity) or picked by the assistant. Separate from the lexical groups on purpose —
     *  these rows carry no highlighted match, because there is none. */
    searchSuggestions: 'Suggestions',
    searchAskAi: 'Ask AI about “{query}”',
    searchAskInChat: 'Ask in chat',
    searchAiAnswer: 'AI answer',
    imageViewerTitle: 'Image preview',
    imageGone: 'The full-size image could not be loaded',
    documentPreviewFailed: 'The preview could not be loaded',
    dismiss: 'Dismiss',
    notification: 'Notification',
    loading: 'Loading…',
    help: 'Help',
    actions: 'Actions',
    error: 'Error',
    /** Read out with the label of a required field. The asterisk beside the label is decoration. */
    requiredField: 'Required',
    primaryNav: 'Primary',
    collapseNav: 'Collapse navigation to icons',
    expandNav: 'Expand navigation',
    openCommandPalette: 'Open command palette',
    skin: 'Skin',
    skinCycle: 'click to switch',
    // Human names for the compiled skins, keyed by SkinDefinition.nameKey: the id is a directory and an
    // attribute value and is never shown. Every locale names the skins in its own language, so cs and sk
    // translate rather than copy these, and a key missing from one dictionary renders `undefined`
    // (tests/lib/skins.test.ts checks all three).
    skinNames: {
      studioLight: 'Light',
      studioOled: 'Dark',
      umberNight: 'Umber Night',
      studioGraphite: 'Graphite',
    },
    logout: 'Sign out',
    language: 'Language',
    rangeLabel: 'Date range',
    rangeLastHour: 'Last hour',
    rangeLast6Hours: 'Last 6 hours',
    rangeLast24Hours: 'Last 24 hours',
    rangeIncomplete: 'Enter both the start and end of the range.',
    rangeInvalidInstant: 'Enter a valid date and time. This local time may not exist during a clock change.',
    rangeInvalidOrder: 'The start must be before the end.',
    rangeOutsideBounds: 'The range is outside the allowed time bounds.',
    rangeToday: 'Today',
    rangeLast7: 'Last 7 days',
    rangeLast30: 'Last 30 days',
    rangeLast90: 'Last 90 days',
    rangeAll: 'All time',
    rangeCustom: 'Custom',
    rangeFrom: 'From',
    rangeTo: 'To',
    filterAllProjects: 'All projects',
    filterProjectsAria: 'Project filter',
    // The canonical page toolbar's condensed filter control (components/ui/PageFilters.tsx). `filters`
    // is the trigger's visible label; `filtersWithCount` replaces its accessible name once something is
    // filtering, because the count beside the label is a decorative badge a screen reader would read as
    // a bare number.
    filters: 'Filters',
    filtersWithCount: 'Filters, {count} active',
    filterOptions: 'Options',
    filterOptionsWithCount: 'Options, {count} filters active',
    filtersActive: 'Active filters',
    filtersClear: 'Clear filters',
    filterRemove: 'Remove filter {name}',
  },
  // The one pagination vocabulary of the app. Every register footer reads from here, so a locale says
  // "previous" with a single word instead of the four competing ones the copy-pasted pagers drifted into.
  // `previous`/`next` are the visible labels; `previousPage`/`nextPage` are the accessible names the
  // buttons keep when a narrow container collapses them to icons.
  pagination: {
    label: 'Pagination',
    previous: 'Previous',
    next: 'Next',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    range: '{from}–{to} of {total}',
    pageLabel: 'Page {page} of {pages}',
    // The rows-per-page select. It is both the visible label and the accessible name: a bare "25"
    // standing beside a page count reads as a second page number.
    perPage: 'Per page',
  },
  // The shared environment-operation progress window. The step names are the ids the daemon declares on
  // the operation row, so a plan that gains a step gets its wording here and nowhere else.
  operationDock: {
    history: 'Open history',
    "region": "Background operations",
    "more": "+{count}",
    "started": "{action}: started",
    "done": "{action}: done",
    "failed": "{action}: failed",
    "dismiss": "Dismiss",
    "ackFailed": "Could not dismiss the operation. Please try again.",
    "updateCore": "Updating Chetty",
    "updatePlugin": "Updating {name}",
    "installPlugin": "Installing {name}",
    "repairPlugin": "Repairing {name}",
    "captioned": "{action}: {caption}"
  },
  terminalSurface: {
    pasteFailed: 'The browser could not read the clipboard. Please use the browser’s Paste command.',
    errors: {
      TERMINAL_REFUSED: 'The terminal request was refused. Please check your access to this Project.',
      TERMINAL_UNSUPPORTED: 'Interactive terminals are not supported on this target.',
      TERMINAL_LIMIT: 'The maximum number of terminals has been reached.',
      TERMINAL_UNKNOWN_SESSION: 'This terminal session no longer exists.',
      TERMINAL_RUNNING: 'The terminal is still running. Close it before dismissing it.',
      TERMINAL_UNAVAILABLE: 'The terminal is currently unavailable.',
      TERMINAL_FAILED: 'The terminal failed. Please open a new session.',
    },
    region: 'Terminal',
    ended: 'The session has ended.',
    endedWithCode: 'The session has ended (exit code {code}).',
    openedElsewhere: 'This terminal is open in another tab.',
    takeOver: 'Take over',
    refused: 'The terminal could not be opened.',
  },
  // The shared formatted-text field (components/ui/RichTextEditor.tsx): its toolbar and link popover.
  richTextEditor: {
    toolbar: 'Formatting',
    bold: 'Bold',
    italic: 'Italic',
    link: 'Link',
    bulletList: 'Bulleted list',
    orderedList: 'Numbered list',
    linkAddress: 'Link address',
    linkInvalid: 'Enter a web or email address.',
    applyLink: 'Apply',
    removeLink: 'Remove link',
  },
  operationProgress: {
    errors: {
      admin_required: 'This action requires an instance administrator.',
      invalid_network: 'Enter a valid network mode and ports from 1 to 65535.',
      network_port_conflict: 'A host port is already in use. Choose a different port or release it before starting the environment.',
      network_port_check_failed: 'Host port availability could not be verified. The environment was not started.',
    },
    progressLabel: 'Operation progress',
    stepOf: 'Step {current} of {total}',
    indeterminate: 'In progress',
    preparing: 'Preparing…',
    succeeded: 'Done',
    failed: 'The operation failed',
    minimize: 'Minimize',
    retry: 'Retry',
    showLog: 'Show log',
    hideLog: 'Hide log',
    logLabel: 'Environment log',
    reopen: 'Show progress',
    steps: {
      image: 'Preparing the base image',
      storage: 'Preparing storage',
      container: 'Creating the container',
      boot: 'Starting the container',
      ready: 'Waiting for the system to come up',
      initialize: 'Initializing the project',
      quiesce: 'Finishing running work',
      stop: 'Stopping the container',
      remove: 'Removing the old container',
      capture: 'Capturing the snapshot',
      record: 'Recording the snapshot',
      import: 'Restoring the data',
      switch: 'Switching over',
      cleanup: 'Cleaning up',
      apply: 'Applying the limits',
      containers: 'Removing containers',
      records: 'Removing records',
      discard: 'Removing the snapshot data',
    },
    actions: {
      start: 'Starting the environment',
      stop: 'Stopping the environment',
      restart: 'Restarting the environment',
      recreate: 'Recreating the environment',
      delete: 'Deleting the project',
      snapshot: 'Creating a snapshot',
      restore: 'Restoring a snapshot',
      deleteSnapshot: 'Deleting a snapshot',
      limits: 'Applying resource limits',
      network: 'Applying network settings',
      rename: 'Moving the project directory',
    },
  },
  auth: {
    loginFailed: 'Could not sign in. Check your credentials.',
    loginRateLimited: 'Too many sign-in attempts. Try again later.',
    signIn: 'Sign in',
    signInDescription: 'Sign in to your Chetty account.',
    orContinueWith: 'Or continue with',
    microsoft: 'Microsoft',
    usernamePlaceholder: 'Username',
    passwordPlaceholder: 'Password',
    ssoErrors: {
      no_account: 'A Chetty account does not exist for this Microsoft account yet. Please ask an administrator to create it.',
      denied: 'Microsoft sign-in was denied. Please contact an administrator.',
      already_linked: 'This Microsoft account is already linked to another Chetty account.',
      directory_unavailable: 'We could not verify your access in the Microsoft directory. Please try again later.',
      tenant_mismatch: 'This Microsoft account does not belong to the organization configured for this instance.',
      state_expired: 'The sign-in attempt expired. Please try again.',
      not_setup: 'Setup is not finished yet. Please sign in with your username and password.',
      sso_failed: 'Microsoft sign-in failed.',
    },
    setupPendingTitle: 'Setup not finished',
    setupPendingBody: "This server has no account yet. Finish the installation in the terminal, then reload this page.",
    setupPendingHint: "Run it on the machine where Chetty is installed.",
  },
  licence: {
    starterTitle: 'Starter plugins',
    starterDescription: 'Install the plugins included in your starter set for everyone.',
    starterAction: 'Review starter plugins',
    starterInstall: 'Install plugins',
    starterLater: 'Not now',
    starterInstalling: 'Installing…',
    starterGrants: 'The packages will be verified first. Any new permissions will be asked in the prepared update.',
    pageTitle: 'Licence required',
    reasonMissing: 'No licence is installed on this server.',
    reasonExpired: 'The licence on this server has expired.',
    reasonOffline: 'The licensing server has been unreachable for too long.',
    reasonRefused: 'The licensing server refused this installation.',
    reasonWrongInstance: 'This licence was issued for a different server.',
    reasonInvalid: 'The licence on this server is not valid.',
    instanceId: 'Instance ID',
    instanceIdHint: 'Identifies this server. The licence is bound to it.',
    copyInstanceId: 'Copy instance ID',
    licenceId: 'Licence ID',
    licenceIdHint: 'Quote it when you contact support.',
    copyLicenceId: 'Copy licence ID',
    serverTime: 'Server time',
    localTime: 'Local time',
    support: 'Need help? Write to {url}.',
    keyLabel: 'Activation key',
    keyPlaceholder: 'Enter the activation key',
    activate: 'Activate',
    activating: 'Activating…',
    errorBadKey: 'This key does not look right. Check it and try again.',
    errorForbidden: 'The licensing server refused this key.',
    errorConflict: 'This key is already active on a different server.',
    errorServer: 'The licensing server cannot be reached right now. Try again later.',
    restarting: 'Restarting…',
    cardTitle: 'Licence',
    state: 'Status',
    stateLocked: 'Locked',
    statusHint: 'This server confirms its licence with the licensing server automatically.',
    customer: 'Customer',
    expires: 'Expires',
    expiresHint: 'After this date the service stops until the licence is renewed.',
    pluginCount: 'Plugins',
    lastContact: 'Last server contact',
    lastContactHint: 'When this server last confirmed its licence with the licensing server.',
    neverContacted: 'Never',
    staleWarning: 'The licensing server has not been reached for a while. The installation keeps running until {date}.',
    checkNow: 'Check now',
    checking: 'Checking…',
    replaceKey: 'Replace activation key',
    replaceTitle: 'Replace activation key',
    replaceDesc: 'Enter the new key. The service restarts after activation.',
  },
  advisor: {
    dockTop: 'Dock to top',
    dockBottom: 'Dock to bottom',
    dockPosition: 'Dock position',
    title: 'Assistant',
    close: 'Close',
    dockLeft: 'Dock left',
    dockRight: 'Dock right',
    chatsCount: 'Chats: {count}',
  },
  conversationList: {
    archived: 'Archived',
    relatedItems: 'Related items',
    relatedItemsToggle: 'Related items of {title}',
    relatedItemsError: 'Related items could not be loaded',
    usage: 'Usage',
    mine: 'Mine',
    new: 'New',
    groups: {
      unread: 'New replies',
      pinned: 'Pinned',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      older: 'Older',
      results: 'Results',
    },
    filters: {
      project: 'Project',
      status: 'Status',
      model: 'Model',
      type: 'Type',
      owner: 'Owner',
      noProject: 'No project',
      unavailableProject: 'Unavailable project',
      unreadScope: 'Unread applies only to your own conversations.',
      clearOne: 'Clear {filter}',
      scope: 'Scope',
      selectedValues: '{filter}: {count} selected',
      working: 'Working',
      waiting: 'Waiting',
      unread: 'Unread',
      error: 'Error',
      webCli: 'Web / CLI',
      scheduled: 'Scheduled activity',
    },
    sort: {
      label: 'Sort',
      direction: 'Direction',
      newest: 'Newest',
      tokens: 'Tokens',
      name: 'Name',
      state: 'State',
      model: 'Model',
      owner: 'Owner',
      ascending: 'Ascending',
      descending: 'Descending',
    },
    selectMode: 'Select',
    selectPage: 'Select conversations on this page',
    contextAncestor: 'Shown for context',
    hostedBy: 'Hosted by {name}',
    lastWriter: 'Last writer: {name}',
    waitingForResponse: 'Waiting for a response',
    runningAgents: 'Agents: {count}',
    runningAgentsAtLeast: 'Agents: {count}+',
    newReply: 'New reply',
    exportSelected: 'Export selected',
    exportSeparateFiles: 'Each conversation downloads as a separate file. Your browser may ask you to allow multiple downloads.',
    exportPartial: 'Download requests started: {started}. Failed: {failed}.',
    projectLoadError: 'Projects could not be loaded',
    listLoadError: 'Conversations could not be loaded',
    personalLoadError: 'Your conversation metadata could not be loaded',
    searchLoading: 'Searching conversation history',
    searchBounded: 'Transcript search returns a limited set of the newest matches.',
    resultCount: 'Matching conversations: {count}',
    deleteAllScope: 'All managed conversations across all accounts will be permanently deleted, including channels and delegated conversations, regardless of the current search or filters. This cannot be undone.',
  },
  sessionsPanel: {
    searchLabel: 'Search',
    noMatches: 'No conversation matches the filter',
    untitled: 'Untitled',
    tok: 'tok',
    openInChat: 'Open in chat',
    viewInChat: 'View chat history',
    deleteAll: 'Delete all',
    confirmDeleteAllTitle: 'Delete every session?',
    subAgents: 'Sub-agents',
  },
  /** The sub-agent tree of one conversation, opened from its row menu in both switcher views. Navigation
   *  into what already ran: nothing on these rows starts, continues or deletes anything. */
  subagentBranch: {
    /** A delegation whose transcript retention has already removed. The row still records that the work
     *  happened; it simply has nowhere to go. */
    unavailable: 'Transcript no longer available',
    truncated: 'More rows than can be shown here',
    error: 'Could not load sub-agents',
  },
  dashboard: {
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    heroAsk: 'What can I help you with?',
    stripLabel: 'Today at a glance',
    quickActions: 'Quick actions',
    pillSummary: 'Summarize today',
    pillSummaryPrompt: 'Please summarize what happened in the team today.',
    pillPlan: 'Plan a task',
    pillPlanPrompt: 'Please plan the following task: ',
    pillCosts: 'Check costs',
    pillAgent: 'Start an agent',
    pillAgentPrompt: 'Please start an agent on the following task: ',
    pillFind: 'Find in project',
    pillFindPrompt: 'Please find the following in the project: ',
    pillCapabilities: 'What can you do?',
    pillCapabilitiesPrompt: 'What can you do?',
    showFeed: 'Show activity',
    showPulse: 'Show team pulse',
    showMetrics: 'Show metrics',
    closePanel: 'Close',
    metricsTitle: 'Metrics',
    metricsMonthCost: 'Your cost this month',
    metricsTodayCost: 'Your cost today',
    metricsMonthTokens: 'Tokens in 30 days',
    composerPlaceholder: 'What can I do for you?',
    composerAction: 'Send to Chetty in a new chat',
    recap: {
      label: 'Yesterday and next steps',
      fallback: 'Yesterday you worked on: {sessions}.',
      continueTitle: 'Continue: {title}',
      rotation: 'Recap variants',
      prev: 'Previous recap',
      next: 'Next recap',
      pause: 'Pause recap rotation',
      resume: 'Resume recap rotation',
    },
    last30d: 'Last 30 days',
    presence: {
      offline: 'Offline',
    },
    live: 'live',
    eventStream: 'Activity',
    eventStreamEmpty: 'No activity yet.',
    workingNow: 'Working now',
    pulse: 'Team pulse',
    pulseNobody: 'Nobody around yet',
    pulseSeen: 'last active',
    pulseUnpriced: 'unpriced',
    pulseSpendOff: 'spend unavailable',
    pulseTodayLabel: 'today',
    pulseActivePeople: 'Active users',
    pulseRunningAgents: 'Running agents',
    pulseMemoryHits: 'Memories recalled',
    pulseCacheHit: 'Average share read from cache',
    pulseVsYesterday: 'compared with yesterday',
    pulseNoBaseline: 'no activity yesterday',
    pulseFlat: 'unchanged from yesterday',
    pulseAgentsBusy: 'Sub-agents are working',
    pulseAgentsIdle: 'No delegated work',
    pulseMemoryFoot: 'memory recalls today',
    pulseCacheFoot: 'share of context read from cache',
    pulseColChannel: 'Channel',
    pulseColDoing: 'Working on',
    pulseColCost: 'Cost',
    pulseTeamCost: 'Team cost',
    pulseColTokens: 'Tokens',
    pulseColHits: 'Memories',
    pulseColTurns: 'Turns',
    pulseRingPeople: 'People',
    pulseRingChannels: 'Channels',
    pulseRingContext: 'Context',
    pulseRingCost: 'Cost',
    pulseRingChannelsUnit: 'channels',
    pulseRingContextUnit: 'tokens read',
    pulseRingCostUnit: 'last 30 days',
    pulseRingEmpty: 'No data yet',
    pulseContext: {
      cacheRead: 'Read from cache',
      input: 'New input',
      cacheWrite: 'Save to cache',
      output: 'Output',
    },
    pulseContextHint: {
      cacheRead: 'Part of the context was read from cache, so it cost less than new input.',
      input: 'Context sent for the first time, at the full input rate.',
      cacheWrite: 'The cost of saving context to the cache for later replies.',
      output: 'The model’s reply. The most expensive tokens, and the fewest.',
    },
    pulseColCache: 'Cache',
    surfaces: { api: 'API token', subagent: 'Sub-agent', web: 'web', cli: 'terminal', cron: 'schedule', internal: 'Internal', unknown: 'unknown client' },
    surfaceHint: { internal: 'Turns without a specific request: scheduled tasks, recovery after restart and the daemon’s internal work. Sub-agent turns are billed to whoever requested them.' },
    ev: {
      turn: 'is working',
      someone: 'Someone',
      message: 'Message',
      decision: 'Decision',
      signedIn: 'Signed in',
      accountLinked: 'Account linked',
      accountProvisioned: 'Account created',
      signInDenied: 'Sign-in denied',
      activity: 'Activity',
    },
    finishSetup: {
      cta: 'Connect a provider',
    },
  },
  settings: {
    updateRequestError: 'Could not start the update.',
    updateStarted: 'The update has started. Chetty restarts briefly along the way, and the whole update can take a few minutes. You can follow its progress at the bottom right.',
    restartRequested: 'Restart requested.',
    restartRequestError: 'Could not request the restart.',
    brainModelsLink: 'See the available models in the Models section →',
    models: 'Models',
    plugins: 'Plugins',
    availablePlugins: 'Available plugins',
    brain: 'Chetty AI',
    data: 'Data',
    navigationLabel: 'Settings categories',
    navigationSearch: 'Search settings',
    navigationNoMatches: 'No settings match this search.',
    modelsSectionHint: 'Choose which model handles each task for this instance and which models are available.',
    brainSectionHint: 'Set up the assistant, connected accounts, providers and runtime limits.',
    // Settings → Models → Model roles: every instance-level answer to "which model does what", in one
    // group above the catalog that says which models exist at all.
    modelCatalog: 'Model catalog',
    modelCatalogRefresh: 'Refresh catalog',
    modelCatalogRefreshing: 'Refreshing catalog…',
    modelCatalogRefreshError: 'Catalog refresh failed for at least one provider: {error}',
    modelCatalogRequestError: 'Could not refresh the model catalog.',
    modelCatalogHint: 'Which models the configured providers offer, with their context and output limits.',
    modelRoles: {
      title: 'Model roles',
      hint: 'Which model performs each task for this instance.',
      instanceDefault: 'Default chat model',
      instanceDefaultHelp: 'Select the model for conversations whose users have no personal choice. Personal settings and model permissions still apply.',
      defaultSaveError: 'The default chat model could not be saved.',
      defaultUnavailable: 'Unavailable: {model}',
      providersLink: 'Providers',
      utility: 'Utility model',
      utilityHelp: 'Writes conversation titles, processes Chetty’s long-term memories and sorts them into categories.',
      utilityOff: 'No model: these tasks are disabled',
      digest: 'Daily digest model',
      digestHelp: 'The model creates the daily dashboard summary. Leave it set to inherit to use the utility model.',
      digestSaveError: 'The digest model could not be saved.',
      promptSuggestion: 'Next-message suggestions model',
      promptSuggestionSameModel: 'Same model with cache',
      promptSuggestionHelp: 'The same model with cache gives the most accurate suggestions, but costs one extra agent step. A cheaper model only sees the last 10 messages. Inherit to use the utility model.',
      promptSuggestionSaveError: 'The suggestions model could not be saved.',
      recapLink: 'Recap settings',
      inherit: 'Inherit: {model}',
      inheritUnset: 'Inherit, no utility model is set',
      inherited: 'Inherited',
      embeddingConstraint: 'Search by meaning requires a provider with an API key. Claude and ChatGPT accounts do not offer this.',
      personal: 'Your personal models',
      personalHelp: 'Set your account’s chat, image and context-shortening models in Account → Models.',
      openAccount: 'Open Account',
    },
    dashboard: 'Recap',
    dashboardSectionHint: 'Set up the daily digest, greeting and assistant quick actions.',
    dashboardSection: {
      title: 'Recap',
      recap: 'Personalized recap',
      recapDesc: 'Show each user their recent conversations and yesterday’s events below the message box.',
      digest: 'AI digest',
      digestDesc: 'A model writes a short summary of yesterday and today so far, and suggests next steps.',
      greeting: 'Agent-written greeting',
      greetingDesc: 'The assistant writes the dashboard headline and the question below it in the user’s language.',
      pills: 'Assistant quick actions',
      pillsDesc: 'The assistant suggests quick actions based on each user’s activity.',
      continue: 'Continue pills',
      continueDesc: 'Offer each user their recent conversations to reopen.',
      perDay: 'Runs per day',
      perDayDesc: 'The most digests one user can create per day. The day is split into equal windows, and the digest refreshes on the first visit in each new window.',
      perDayOption: '{n}\u00d7 a day',
      variants: 'Variants per generation',
      variantsDesc: 'How many different summaries of yesterday and today so far each digest generation creates. The dashboard rotates between them. At 1, the same summary is always shown.',
      variantsOption: '{n} per batch',
      countsNote: 'The change applies from the next generation. A digest also refreshes during the day as new activity arrives, at most as many times as the daily limit allows.',
      providerInherit: 'Same as the utility model',
      model: 'Digest model',
      modelDesc: 'The model for the daily digest. Set its role in Settings → Models, alongside the roles it inherits from.',
      modelLink: 'Model roles',
      regenerate: 'Regenerate now',
      regenerateError: 'Could not start regeneration.',
      statusReady: 'Today\u2019s digest is ready',
      statusGenerating: 'Generating\u2026',
      statusUnavailable: 'No digest available yet',
    },
    pluginsSectionHint: 'Configure and monitor the extensions that add tools and platforms.',
    availablePluginsSectionHint: 'Browse the plugin registry and install extensions.',
    access: 'Access',
    accessSectionHint: 'Keys, tokens and passwords that plugins use to connect to their services.',
    dataSectionHint: 'View log files and confirm permanent deletion of operational data.',
    systemSectionHint: 'Monitor the running version, services, updates and session security.',
    logs: 'Logs',
    logsOpen: 'Open logs',
    logsEmpty: 'No log files yet.',
    logsPickFile: 'Choose a log file to view.',
    logsSearch: 'Filter lines\u2026',
    logsMatches: '{n} of {total} lines',
    logsTruncated: 'Showing the last {n} of {total} lines.',
    logsTruncatedCapped: 'Showing the last {n} of {total} lines \u2014 the log viewer\u2019s maximum.',
    logsLoadFull: 'Load the whole file',
    logsError: 'Could not read this log file.',
    logsErrorGone: 'This log file no longer exists.',
    logsErrorTooBig: 'This file is too large to open in the log viewer.',
    logsDeleteFile: 'Delete this log file',
    logsDeleteAll: 'Delete all',
    logsDeleteFileTitle: 'Delete this log file?',
    logsDeleteFileDesc: 'Permanently deletes {name}. If it is today\u2019s file, it is recreated on the next log line.',
    logsDeleteAllTitle: 'Delete all log files?',
    logsDeleteAllDesc: 'Permanently deletes all log files. Today’s files are recreated on the next log entry.',
    logsLevels: 'Log levels',
    logsLevelsAll: 'All levels',
    logsLevelsCount: '{n} of {total} levels',
    conversationDiagnostics: {
      exactAggregate: 'Exact provider aggregate', tokenSegments: 'Prompt token segments',
      segmentTokens: '{role}: ~{n} tokens', promptTokens: '~{n} prompt tokens',
      title: 'Conversation diagnostics',
      description: 'Inspect the exact model requests recorded by this instance.',
      open: 'Open diagnostics',
      captureDisabled: 'Detailed request capture is disabled.',
      enableCapture: 'Enable capture',
      captureConfirmTitle: 'Enable exact request capture?',
      captureConfirmDescription: 'Diagnostics will store exact provider prompts, message content, tool definitions, tool data and model responses. Recorded data may contain private or sensitive information.',
      captureEnableError: 'Exact request capture could not be enabled.',
      sessions: 'Sessions', tools: 'Tools', content: 'Content', mobileView: 'Diagnostics view', requests: 'Requests', messages: 'Messages', contentView: 'Request content', displayMode: 'Display mode',
      searchSessions: 'Search sessions…', searchMessages: 'Search prompt and messages…', searchTools: 'Search tools…',
      filters: 'Filters', userId: 'User ID', userIdInvalid: 'Enter a positive whole number.', surface: 'Surface', provider: 'Provider', model: 'Model', status: 'Status', role: 'Role',
      all: 'All', allRoles: 'All roles', conversation: 'Conversation', channel: 'Channel', subagent: 'Sub-agent',
      pending: 'Pending', succeeded: 'Succeeded', captured: 'Captured', legacy: 'Legacy', error: 'Error', interrupted: 'Interrupted', retry: 'Retry',
      loadMore: 'Load more', loadError: 'Could not load conversation diagnostics.', noSessions: 'No matching sessions.', noRequests: 'No captured requests in this session.',
      legacyWarning: 'Recorded before exact capture was enabled. Transcript is approximate.',
      toolCall: 'Tool call', toolResult: 'Tool result', mediaContent: 'Binary or media content',
      noLegacyData: 'No legacy transcript is available.',
      prompt: 'Prompt', cacheRead: 'Cache read', cacheWrite: 'Cache write', input: 'Input', output: 'Output', reasoning: 'Reasoning', cost: 'Cost', duration: 'Duration',
      cachedPrefixEstimate: 'Estimated cached prefix length', notAvailable: 'Not available',
      inspector: 'Detail', pickMessage: 'Select a prompt or message item.', pretty: 'Pretty', json: 'JSON', copy: 'Copy', rawRequest: 'Raw provider request', payloadTooLarge: 'Data is too large to display in diagnostics.',
      unnamedItem: 'Item', unnamedTool: 'tool',
      noTools: 'No tools were sent in this request.', immediate: 'Immediate', deferred: 'Deferred', server: 'Server',
    },
    /** Labels for the deck hero's metric rail — the hairline strip of figures every settings section
     *  opens with. Each names a count the section itself already holds, so the rail never reports a
     *  number with no editor under it. */
    metric: {
      catalog: 'In the catalog',
      overrides: 'Context overrides',
      accounts: 'Provider accounts',
      connected: 'Connected',
      aiModels: 'AI models',
      pluginWorlds: 'Plugins with a UI',
      pluginPages: 'Plugin pages',
      pluginSections: 'Plugin settings',
      logFiles: 'Log files',
      logVolume: 'Log volume',
      requestCapture: 'Request capture',
      conversationCleanup: 'Conversation cleanup',
    },
    adminOnly: 'Administrators only',
    adminOnlyDesc: 'This section is reserved for admins. Edit your own profile in My account.',
    tokenTtl: 'Login token validity',
    daysPolicy: {
      presets: '{policy} presets',
      preset: '{n} days',
      custom: 'Custom…',
      customFor: 'Custom duration for {policy}',
      days: 'days',
    },
    autoUpdate: 'Automatic updates',
    skins: {
      label: 'Skins',
      hint: 'Which designs accounts can switch between from the top bar. If fewer than two are allowed, the switcher is hidden.',
      none: 'Switching disabled',
      emptyHint: 'With nothing allowed, everyone sees the design this deployment was configured with.',
      builtinGroup: 'Built in',
      customGroup: 'Custom',
      unavailable: 'Not available on this instance',
      custom: {
        label: 'Custom skins',
        hint: 'Skins added to this instance. A skin may only override public design tokens, and it is validated when it is added.',
        manage: 'Manage custom skins',
        none: 'No custom skins',
        count: { one: '{n} custom skin', few: '{n} custom skins', many: '{n} custom skins' },
        remove: 'Remove',
        removeTitle: 'Remove this skin?',
        removeHint: 'The skin folder is deleted from this instance. Anyone wearing it moves to the instance design.',
        refusedBadge: 'Refused',
        takesEffect: 'Adding or removing a skin applies at once, in every page that is open.',
        idLabel: 'Skin id',
        idHint: 'Must start with custom- and names the folder the skin lives in.',
        idPlaceholder: 'custom-sunrise',
        nameLabel: 'Display name',
        namePlaceholder: 'Sunrise',
        baseLabel: 'Built on',
        baseHint: 'The design the skin repaints and whose structure it keeps.',
        tokensLabel: 'Token sheet',
        tokensHint: 'One :root rule overriding public design tokens, for example --radius: 8px;',
        tokensPlaceholder: ':root {\n  --radius: 8px;\n}',
        submit: 'Add skin',
        refusedPrefix: 'The skin was not added:',
        fontFamily: 'Font family',
        fontFile: 'WOFF2 file',
        fontHint: 'One normal variable WOFF2, up to 2 MiB. Use this family in --font-sans or --font-mono.',
        fontUpload: 'Upload font',
        fontRemove: 'Remove font',
        fontBusy: 'Saving font…',
      },
    },
    pushContact: 'Push notification contact',
    pushContactPlaceholder: 'https://your-site.com or mailto:you@example.com',
    system: 'System',
    version: 'Chetty version',
    updateAvailable: '{v} available',
    upToDate: 'Up to date',
    lastUpdateSucceeded: 'Last update completed successfully on {date}',
    updateNow: 'Update now',
    checkUpdates: 'Check for updates',
    updating: 'Updating…',
    services: 'Services',
    serviceDaemon: 'Service',
    serviceWeb: 'Web',
    serviceUp: 'Running',
    serviceDown: 'Down',
    restartDaemon: 'Restart service',
    restartWeb: 'Restart web',
    restartDaemonTitle: 'Restart the service?',
    restartDaemonDesc: 'Briefly interrupts the API and running conversations.',
    restartWebTitle: 'Restart the web?',
    restartWebDesc: 'Briefly interrupts the web interface; the API keeps running.',
    restartConfirm: 'Restart',
    unreadableTitle: 'The settings cannot be read',
    unreadableDesc: 'The stored instance settings are damaged. They have been left unchanged until you reset them explicitly. Nothing is saved, and nothing shown here is the stored configuration. {reason}',
    unreadableReasonJson: 'The stored row is not valid JSON.',
    unreadableReasonObject: 'The stored row is not a settings document.',
    unreadableReasonValue: 'A stored value is invalid.',
    unreadableAdminOnly: 'Ask an administrator to reset them.',
    unreadableReset: 'Reset settings',
    unreadableResetTitle: 'Reset the instance settings?',
    unreadableResetDesc: 'The damaged settings, including provider keys, are replaced by the defaults and cannot be recovered afterwards. Providers and keys have to be entered again.',
    unreadableResetConfirm: 'Reset',
    unreadableResetPending: 'Resetting',
    restartDaemonStarted: 'The service is restarting…',
    restartWebStarted: 'The web is restarting…',
    systemDiagnostics: 'System diagnostics',
    systemDiagnosticsHint: 'Current CPU, memory, disk, IO and network usage and service uptime. Open a reading\u2019s history by its ring.',
    diagnosticCpu: 'CPU',
    diagnosticMemory: 'Memory',
    diagnosticUptime: 'Uptime',
    diagnosticDisk: 'Disk',
    diagnosticIo: 'IO',
    diagnosticNetwork: 'Network',
    historyTitle: 'Load history',
    historyOpen: 'Open {metric} history',
    historyMetrics: 'Resources',
    historyShare: 'Capacity shares in %; IO in % of its limit or MB/s without one; network in Mb/s',
    historyEmpty: 'No readings are available for the selected period.',
    historyNotStarted: 'Recording has not started yet.',
    historyGapsHelp: 'Missing or unavailable readings leave gaps. They do not mean zero load.',
    historyAdjusted: 'The displayed period was adjusted to the available history and server time.',
    historyStale: 'Last known figures',
    historyError: 'Load history could not be loaded.',
    error_invalid_history_query: 'The history filters are invalid.',
    error_invalid_history_range: 'Select a valid start and end of the period.',
    error_range_outside_retention: 'The selected period is outside the retained history of the last 30 days.',
    error_unsupported_history_resolution: 'This interval cannot represent the selected period.',
    error_invalid_history_response: 'The history response is invalid. Please try again.',
    retention: {
      label: "Automatic conversation cleanup",
      hint: 'Removes old idle conversations every hour. Running, active, and channel sessions are never deleted.',
      olderThan: "Older than",
      days: "days",
    },
    on: 'On',
    off: 'Off',
    modelSearchPlaceholder: 'Search models…',
    modelNoMatches: 'No models match this search.',
    modelCatalogEmpty: "No models are available.",
  },
  projects: {
    newProject: 'New project',
    registry: 'Project registry',
    workspaceIntro: 'Projects, repository status and automation in one place.',
    registryReady: 'Registry available',
    metricProjects: 'projects',
    metricIcons: 'custom icons',
    metricDocumented: 'documented',
    searchPlaceholder: 'Search projects, paths or notes…',
    menuSearch: 'Search projects…',
    menuNoMatch: 'Nothing found',
    searchLabel: 'Search projects',
    searchClear: 'Clear the project search',
    openProject: 'Open project {slug}',
    noMatches: 'No projects match this search.',
    tableLabel: 'Project registry',
    cardOpen: 'Open',
    runtimeManaged: 'Managed',
    runtimeHost: 'Host',
    hostStateTitle: 'Host project',
    hostStateHint: 'Runs in its host directory, without a container of its own.',
    branchLabel: 'Checked-out branch',
    teamStrip: 'People with access to {slug}',
    membersCount: '{n} with access',
    teamEmpty: 'No one has access',
    teamMore: '{n} more',
    locationHostTitle: 'Host directory',
    locationGuestRoot: 'Path in environment',
    locationAdoptedFrom: 'Converted from',
    detailSections: 'Project sections',
    tabOverview: 'Overview',
    tabAccess: 'People',
    groupLocation: 'Location',
    groupDetails: 'Details',
    accessTitle: 'User access',
    accessHint: 'Choose the member accounts that can use this project. Administrators automatically have access to every project.',
    accessCount: '{n} of {total} users have access',
    accessEmpty: 'No member accounts are available',
    addMember: 'Add member',
    removeMember: 'Remove access',
    removeMemberTitle: 'Remove access?',
    removeMemberBody: '{name} loses access to {project}, including its files, worktrees and stored credentials. Their own conversations and memories stay.',
    memorySharedTitle: 'Shared memory',
    memorySharedDesc: "The selected people will share this project's memory. The agent uses it in the project alongside their private memories.",
    memoryEveryone: 'If nobody is selected, every project member shares the memory.',
    memoryEveryoneShort: 'Everyone',
    memorySharersTitle: 'Who shares the memory',
    memorySharersGroup: 'Sharers',
    memorySharersCount: '{n} people sharing',
    memoryPickTitle: 'Shared memory access',
    memoryPickHint: 'Pick the members who share this project\'s memory pool. Leave empty to share with everyone assigned to the project.',
    loadError: 'Failed to load projects',
    empty: 'No projects',
    fieldSlug: 'Slug',
    rename: 'Rename',
    renameTitle: 'Rename project',
    renameMovesDirectory: 'The project directory inside the environment moves from {from} to {to}. A running environment restarts for this: work in progress stops and its services start again.',
    slugTaken: 'A project with this name already exists. Choose another name.',
    slugReserved: 'This name is reserved by the project environment. Choose another name.',
    renameWorktreesActive: 'Remove this project\'s worktrees before renaming it: they refer to the current project directory.',
    fieldPath: 'Path',
    fieldNotes: 'Project notes',
    create: 'Create',
    removeConfirmTitle: 'Remove project',
    removeConfirmBody: 'Remove project "{slug}", its environment and associated resources? Files will be handled as listed below. This action cannot be undone. Private conversation history is preserved.',
    deletionDeleteFiles: 'Also delete files',
    deletionDeleteFilesHint: 'Also removes the project working directory on the host or paired computer, except folders listed for preservation.',
    deletionKeepFilesHint: 'Files in the project working directory will be preserved, except for plugin-owned storage listed below.',
    deletionManagedDisk: 'The managed environment disk and plugin-owned storage are always deleted, even when this option is off.',
    deletionPreservedPaths: 'Folders whose files will be preserved:',
    deletionPreservedReasons: {
      protected: 'Files will not be deleted: protected system, home or application folder.',
      overlap: 'Files will not be deleted: the folder overlaps another live project.',
    },
    deletionPreservedBody: 'The project and its associated resources will still be removed. Files in these folders will remain unchanged.',
    deletionScopeChanged: 'Deletion scope changed. Review the updated inventory and confirm it again.',
    deletionPaths: 'Folders and all their contents to remove:',
    deletionItems: 'Associated resources to remove:',
    deletionUnavailable: 'Unavailable owners: {owners}. Their resources cannot be listed now. Deletion will remain blocked until these owners are available.',
    deletionConfirmFiles: 'By confirming, you agree to permanently remove the folders listed for removal and all their contents, including folders that existed before this project, adopted host folders and paired working directories.',
    deletionPending: 'Deleting project',
    deletionBlocked: 'Deletion blocked',
    deletionInactive: 'This project is inactive while deletion is in progress.',
    deletionContinue: 'Continue deletion',
    removeConfirmBtn: 'Remove',
    slugPlaceholder: 'my-project',
    pathPlaceholder: '~/projects/my-app',
    browse: 'Browse',
    pickFolder: 'Pick the project folder',
    parentFolder: 'Up one level',
    selectFolder: 'Select this folder',
    folderEmpty: 'No sub-folders',
    folderError: 'Could not read the folder',
    pathMissing: 'Directory missing',
    newFolder: 'New folder',
    folderName: 'Folder name',
    folderNamePlaceholder: 'Folder name',
    createFolder: 'Create folder',
    folderExists: 'A folder with this name already exists.',
    folderCreateError: 'The folder could not be created. Check the name and permissions, then try again.',
    folderCreated: 'Folder "{name}" created.',
    ctxCopyPath: 'Copy path',
    ctxPathCopied: 'Project path copied',
    ctxRemove: 'Remove project',
    adoptProject: 'Convert to managed environment',
    adoptConfirm: 'The project will use a managed environment. On first start, its workspace moves to the project volume. You can undo this only before the first start.',
    undoAdoption: 'Undo managed conversion',
    undoAdoptionConfirm: 'Return this unstarted project to its original host folder?',
    managed: 'Managed environment',
    host: 'Host computer folder',
    computer: 'Computer',
    computerActivity: 'Activity',
    executionKind: 'Execution target',
    privateHint: 'This project stays private until you share it. Its environment starts on first use.',
    defaultBadge: 'Default',
    defaultBadgeHint: 'Your default project, first in the order. Chat platform conversations where you have not chosen a project open in it.',
    reorderHint: 'Hold a card’s move handle and drag it to change the order; the first project is the default.',
    reorderHandle: 'Move project {slug}',
    reorderRole: 'movable project',
    reorderInstructions: 'Press space or Enter to pick up a project, use the arrow keys to move it, and press space or Enter to drop it. Press Escape to cancel.',
    reorderPickedUp: 'Picked up project {name}. Position {position} of {total}.',
    reorderMoved: 'Project {name} is at position {position} of {total}.',
    reorderDropped: 'Dropped project {name} at position {position} of {total}.',
    reorderCancelled: 'Moving project {name} was cancelled.',
    createGrant: 'Allow additional projects',
    createGrantHint: 'The account may create projects beyond its default one.',
    shareGrant: 'Allow project invitations',
    shareGrantHint: 'The account may invite other people into its projects.',
    limit: 'Managed project limit',
    limitHint: 'Includes the default project. Administrators are not subject to these account limits.',
    permissions: 'Project permissions',
    invite: 'Invite member',
    userId: 'Account ID',
    userIdHint: "Ask for the person's account ID. Only administrators can view the account list.",
    member: 'Account {id}',
    accessCountMembers: { one: '{n} user has access', few: '{n} users have access', many: '{n} users have access' },
    sharingWarning: 'Every member can access all existing project files, worktrees, browser profiles and credentials stored in the environment. Members have the same rights to manage and delete the project. Private conversations, memories and personal integrations are not shared automatically.',
    computerSharingWarning: 'Every member works on this computer as the operating system user that runs its Chetty connection: they can read and change that user\'s files and run commands on it. Private conversations and memories are not shared automatically.',
    computerMembersOwnerOnly: "Only the computer's owner manages its members.",
    inviteDenied: 'To invite members, you need sharing permission from an administrator.',
  },
  users: {
    projectDeleting: 'The project is being deleted. Its access cannot be changed now.',
    existingProjects: 'Existing projects',
    newPersonalProject: 'New personal project',
    presets: {
      detachConfirm: "Change permission",
      apply: "Overwrite everyone in this preset",
      title: "Account presets",
      button: "Account presets",
      new: "New preset",
      name: "Preset name",
      create: "Create preset",
      delete: "Delete preset",
      deleteTitle: "Delete account preset?",
      deleteBody: "Delete “{name}” and detach {n} accounts? Accounts keep their current permissions and personal settings.",
      deleteError: "The preset could not be deleted.",
      choice: "Preset",
      accountsHint: "How many accounts use the preset right now.",
      accounts: "Accounts",
      identity: "Preset",
      nameHint: "The name the preset is offered under on accounts.",
      nameTaken: "A preset with this name already exists.",
      intro: "A preset sets the permissions and default behavior of the accounts that use it.",
      choiceHint: "The account takes its permissions and settings from the preset until one of them is changed.",
      none: "None",
      newAccount: "Preset for new accounts",
      newAccountHint: "New human accounts receive this preset. Without a preset they start with no permissions.",
      empty: "No account presets yet.",
      loadError: "Account presets could not be loaded.",
      search: "Search presets",
      section: "Preset sections",
      rights: "Permissions",
      behavior: "Behavior",
      behaviorHint: "Unset values inherit the instance defaults. Personal account settings take priority.",
      projectsHint: "These Projects are the minimum membership for attached accounts. Extra memberships remain.",
      accountCount: "{n} attached accounts",
      inherit: "Inherit instance default",
      inherited: "Inherited from instance defaults",
      detachTitle: "Detach account from preset?",
      detachBody: "This permission change detaches the account from “{name}”. Future preset updates will no longer apply.",
      applyTitle: "Overwrite settings in this preset?",
      applyBody: "Clear the own value of “{field}” on {n} accounts attached to “{name}”? They will inherit the preset value.",
      retainedProjects: "The last active Project was retained for these accounts: {projects}",
    },
    defaults: {
      button: 'Default settings for users',
      title: 'User defaults',
      intro: "Base account behavior. Personal values and preset behavior take priority.",
      navLabel: 'Default settings sections',
      navSearch: 'Search sections',
      navNoMatches: 'No default settings match this search.',
      ownCount: 'Accounts with their own value: {n}',
      apply: 'Apply to everyone',
      applyTitle: 'Apply to everyone?',
      applyBody: "Delete the own value of “{field}” on {n} accounts? They will inherit their preset value where set, otherwise the instance default.",
      applied: 'Accounts now inheriting the default: {n}',
      loadError: 'The defaults could not be loaded.',
      missingModel: 'The model is no longer offered',
      noChoice: 'No model',
      autoCompactAtHint: 'The share of the context window at which a conversation is compacted.',
      advisorStyleHint: 'The register the agent writes in when this account asks for nothing else.',
      localeHint: 'The language the interface and the agent’s replies start in.',
      rulesHint: 'The permission rules, the YOLO default and how an unattended question is answered. All three are one record: an account holds them together or inherits them together.',
      pluginsEmpty: 'No installed plugin offers per-account settings.',
      sectionModels: 'Models',
      sectionMemory: 'Memory',
      sectionSkills: 'Skills',
      sectionStyle: 'Style and instructions',
      sectionLanguage: 'Language',
      sectionRules: 'Rules and YOLO',
      sectionPlugins: 'Plugins',
    },
    projectRequired: 'An account requires at least one active Project.',
    lastActiveProject: 'Assign another active Project to these accounts first. Otherwise they would lose their last active Project:',
    projectInactive: 'The selected Project is no longer active. Select another Project.',
    projectsLoadError: 'Projects could not be loaded.',
    noActiveProjects: 'No active Project is available. Select a new personal Project.',
    workspaceEyebrow: 'Access directory',
    workspaceIntro: 'Accounts, roles, project access, and model and tool permissions in one place.',
    workspaceReady: 'Directory synchronized',
    metricUsers: 'users',
    metricAdmins: 'administrators',
    searchPlaceholder: 'Search users…',
    searchLabel: 'Search users',
    searchClear: 'Clear user search',
    openUser: 'Open user {name}',
    manageProjects: 'Manage project access',
    manageModels: 'Manage allowed models',
    managePlugins: 'Manage granted plugins',
    manageTools: 'Manage tool access',
    noMatches: 'No users match this search',
    tableLabel: 'User directory',
    role: 'Role',
    createdAt: 'Created',
    webActivityOnline: 'Online in admin',
    webActivityAgo: 'Last here {time} ago',
    webActivityDate: 'Last here {date}',
    webActivityNever: 'No admin visit yet',
    sectionAccount: 'Account',
    sectionPermissions: 'Permissions',
    detailSections: 'Account sections',
    fromPreset: 'From preset',
    projectDeletingState: 'Deleting',
    loadError: 'Failed to load users',
    empty: 'No users',
    emptyDescription: 'Create the first account to get started.',
    fieldUsername: 'Username',
    fieldName: 'Display name',
    usernameTaken: 'That username is already taken',
    addUser: 'Add user',
    newUser: 'New user',
    create: 'Create',
    delete: 'Delete',
    createError: 'The user could not be created.',
    createInvalid: 'Enter a valid username and a password of at least eight characters.',
    deleteError: 'The user could not be deleted.',
    deleteLastUser: 'The last user account cannot be deleted.',
    deleteAdmin: 'Remove the administrator role before deleting this user.',
    deletePairedComputers: 'This account has paired computers. Have their owner confirm removal of their Projects first, then delete the account.',
    deleteActiveProcesses: 'The user still has active processes. Stop them before deleting the account.',
    deleteCleanupError: 'The user could not be deleted because plugin data cleanup failed.',
    userNotFound: 'The user no longer exists.',
    adminRequired: 'Administrator permission is required for this action.',
    cannotDemoteLastAdmin: 'The last administrator cannot be demoted.',
    confirmDeleteTitle: 'Delete {name}?',
    confirmDeleteDesc: 'Permanently delete the user and all their data: settings, memories, personality, and project access. This cannot be undone.',
    projects: 'Projects',
    admin: 'Administrator',
    member: 'Member',
    chatbot: 'Chatbot',
    chatbotHint: 'This account has no self-service sign-in. A signed-in administrator can enter it, but it cannot become an administrator.',
    makeAdmin: 'Make admin',
    removeAdmin: 'Remove admin',
    confirmMakeAdminTitle: 'Make {name} an administrator?',
    confirmMakeAdminDesc: 'This gives access to user administration, instance settings, and all administrator-only actions.',
    confirmRemoveAdminTitle: 'Remove administrator access from {name}?',
    confirmRemoveAdminDesc: 'This immediately removes access to user administration, instance settings, and all administrator-only actions.',
    confirmSelfDemotionDesc: 'You will immediately lose access to Users, instance settings, and all administrator-only actions. Only another administrator can restore your role.',
    allowedModels: 'Allowed models',
    allModels: 'All models',
    allModelsHint: 'Every model this installation offers, including models added later.',
    modelGrantHint: 'Select the models this user may run. With none selected, the user may run no model.',
    noModelsGranted: 'No model allowed',
    modelNotOffered: 'Not offered',
    updateError: 'Failed to save the change',
    ctxImpersonate: 'Sign in as',
    impersonateError: 'Could not sign in as this user',
    impersonatingAs: 'Viewing the app as {name}',
    stopImpersonating: 'Back to your account',
    stopImpersonateError: 'Could not return to your account',
    grantedPlugins: 'Granted plugins',
    grantedPluginsHint: 'System features such as schedules and skills are granted to individual users. Users, including administrators, cannot use them without access.',
    grantedPluginsCount: '{n} of {total} granted',
    grantedPluginsSelected: '{n} granted',
    grantedPluginsEmpty: 'No plugin is handed out per user.',
    tools: 'Tools',
    toolsHint: "Tools available to this user in their own chat. Checked plugin tools have been granted to them. New accounts have none until an administrator grants them. Built-in tools can be enabled or disabled too. Automatic recall needs MemorySearch and automatic saving needs MemoryAdd. Grant the relevant plugin to make an unavailable tool available. Access to shared Discord channels is set by the plugin's role rules, not this list.",
    toolsEmpty: 'No tools available',
    statMemories: 'Memories',
    statSessions: 'Sessions',
    statTopModel: 'Top model',
    noMemories: 'No memories',
    noSessions: 'No sessions yet',
    noModel: 'No model yet',
    statTopModelHint: 'The model the account uses most.',
    statSessionsHint: 'The number of conversations the account has had.',
    statMemoriesHint: 'How many memories the assistant keeps for the account.',
    createdAtHint: 'When the account was created.',
    roleHint: 'Administrators manage the instance; members work in the projects they are given.',
    fieldUsernameHint: 'The account login name. It must be unique.',
    fieldNameHint: 'The name the account is shown under in lists and conversations.',
    projectsNone: 'No project is assigned to this account yet.',
    addProject: 'Add project',
    removeProject: 'Remove access',
    removeProjectTitle: 'Remove project access?',
    removeProjectBody: 'The account loses access to {project}, including its environment, files and stored credentials.',
    groupAccess: 'Models and tools',
    groupActivity: 'Activity',
    groupIdentity: 'Identity',
  },
  managePicker: {
    manage: 'Manage',
    searchPlaceholder: 'Search…',
    all: 'All',
    none: 'None',
    selectedCount: '{n} selected',
    saveChanges: 'Save changes',
    noResults: 'No results',
    filterByGroup: 'Filter by group',
    modelsCount: '{n} models · {p} providers',
    allModelsCount: 'All models allowed · {n} available',
    modelsSelected: '{n} models selected',
    projectsCount: '{n} of {total} projects assigned',
    projectsSelected: '{n} projects selected',
    projectsSubtitle: 'Projects this user can access.',
    toolsCount: '{n} of {total} tools enabled · {p} plugins',
    toolsSelected: '{n} tools selected',
    toolsSubtitle: "Enable or disable this account's tools.",
    familyGrant: 'Allowed by pattern',
    familyGrantHint: 'This tool is covered by a family grant. Edit the account\'s grant pattern to revoke it.',
    notGranted: 'not granted',
    notGrantedHint: 'This user cannot run the tool because they do not have access to its plugin. Grant the plugin to make it available.',
    toolGroupElowen: 'Chetty',
    toolGroupMemory: 'Memory',
  },
  vault: {
    share: "Share",
    shareItem: "Sharing for {name}",
    sharedWith: { one: "Shared with {count} person", few: "Shared with {count} people", many: "Shared with {count} people" },
    sharedBy: "Shared by {name}",
    shareSearch: "Find a person",
    addAccess: "Share with {name}",
    revokeAccess: "Revoke access: {name}",
    title: "Vault",
    hint: "The agent sees only names and descriptions and writes {{vault:NAME}}; saved values are never shown again.",
    listTitle: "Your items",
    add: "Add item",
    describe: "Edit description",
    replace: "Replace value",
    deleteTitle: "Delete this item?",
    deleteBody: "The item {name} will no longer be available to the agent, and its recorded usage is deleted.",
    name: "Name",
    nameHint: "A–Z, 0–9 and _, starting with a letter, up to 64 characters.",
    description: "Description",
    descriptionHint: "Describe what the agent should use this item for (1–500 characters).",
    value: "Value",
    valueHint: "At least 4 characters, at most 64 KiB. The value is saved exactly as entered.",
    multiline: "Multi-line value",
    empty: "No items yet",
    emptyHint: "Add a credential the agent can use without seeing its value.",
    updated: "Last changed",
    listHint: "The agent sees names and descriptions; saved values are not shown again. It uses owned and shared items as vault:NAME inside double braces. Names must be unique in your vault. Share only with people you trust: a recipient can extract the value through a tool. Revoking access blocks future use but cannot remove a value from a running process. If a credential leaks, rotate it with its provider too.",
    shareConflict: "This person already has an item with the same name.",
    shareFailed: "Sharing could not be changed.",
    shareLoadFailed: "Sharing could not be loaded.",
    lastUsed: "Last used",
    never: "Never",
    usageByPerson: "Usage by person",
    deletedActor: "Deleted account #{id}",
    statsLoadError: "Usage could not be loaded",
    chartTitle: "Uses per day",
    chartUses: "Uses",
    totals: { one: "{n} use", few: "{n} uses", many: "{n} uses" },
    exists: "An item with this name already exists.",
    notFound: "This item no longer exists. Reload the list.",
    unavailable: "The vault is unavailable. Please try again later.",
    chatbot: "The vault is not available for chatbot accounts.",
    failed: "The item could not be saved. Please try again.",
    validation: "Check the name, description and value against the field rules.",
  },
  apiTokens: {
    sectionHint: 'Personal API tokens let your own tools drive the agent over HTTP, with your access.',
    listTitle: 'Your tokens',
    listHint: 'Each token authenticates as you, with your access.',
    create: 'New token',
    createTitle: 'New API token',
    name: 'Name',
    namePlaceholder: 'CI pipeline',
    expires: 'Expires',
    expiresHelp: 'The token stops working at the end of this UTC day. Leave it empty for one that lasts until you delete it.',
    expiresPast: 'The expiry day cannot be in the past.',
    expiresNever: 'No expiry',
    createSubmit: 'Create token',
    createError: 'The token could not be created',
    revealedTitle: 'Copy your token now',
    revealedHint: 'This is the only time it is shown. Chetty keeps only a hash of it, so it cannot be read back later.',
    copy: 'Copy',
    copied: 'Copied',
    done: 'Done',
    columns: {
      lastUsed: 'Last used',
      expires: 'Expires',
    },
    never: 'Never',
    costUnknown: 'Cost unknown',
    emptyTitle: 'No tokens yet',
    emptyDescription: 'Create one to call the agent from your own scripts or CI.',
    loadError: 'Your tokens could not be loaded',
    statsLoadError: 'Usage could not be loaded',
    chartTitle: 'Turns and spend per day',
    chartTurns: 'Turns',
    chartAnswered: 'Answered',
    chartErrors: 'Failed',
    chartCost: 'Cost',
    chartEmpty: 'Nothing recorded in this window',
    chartCostUnknown: 'Some turns in this window reported no cost, so their total is unknown rather than zero.',
    totals: '{requests} requests · {turns} turns · {answered} answered · {failed} failed · {tokens} tokens · {cost}',
    limitsTitle: 'Limits',
    limitsHint: 'Every limit is optional. A limit switched to unlimited is not enforced.',
    limitsError: 'The limits could not be saved',
    unlimited: 'Unlimited',
    limits: {
      dailyCostMicrousd: 'Cost/day',
      monthlyCostMicrousd: 'Cost/month',
      requestsPerMinute: 'Requests/min',
      maxConcurrentTurns: 'Concurrent',
    },
    limitHints: {
      dailyCostMicrousd: 'A day is a UTC day. Once the recorded cost reaches this, turns are refused. A day with a turn that reported no cost refuses too, because its spend cannot be checked.',
      monthlyCostMicrousd: 'A month is a UTC calendar month. Once the recorded cost reaches this, turns are refused until the next month begins. A month with a turn that reported no cost refuses too, because its spend cannot be checked.',
      requestsPerMinute: 'Applies to every request this token authenticates, whatever it asks for.',
      maxConcurrentTurns: 'How many turns this token may have running at the same time.',
    },
    regenerateTitle: 'Create a new token value?',
    regenerateBody: '“{name}” keeps its name, limits and recorded usage, but it gets a new value: the current one stops working immediately and everything using it has to be updated.',
    regenerate: 'Create a new value',
    regenerateError: 'The new token value could not be created.',
    delete: 'Delete',
    deleteTitle: 'Delete this token?',
    deleteBody: 'Everything using “{name}” stops working immediately, and its recorded usage is deleted.',
    deleteError: 'The token could not be deleted',
    callTitle: 'Calling the agent',
    callHint: 'Send the token as a bearer header to /api/v1/agent/runs on this host. The answer names its conversation; pass that id back as conversationId to continue it. Add "stream": true to receive progress as server-sent events.',
  },
  elowenCli: {
    sectionHint: 'Connect your computers to Chetty. The agent works on them with files and commands.',
    approveTitle: 'Approve sign-in',
    accountApproveTitle: 'Approve server CLI sign-in',
    accountApproveHint: 'This terminal gets a personal API token with access to your account. No computer is paired. Revoke access in Account → API tokens.',
    accountApproved: 'Server CLI sign-in approved',
    terminal: 'Terminal',
    approveHint: 'This device gets full access to your account.',
    device: 'Device',
    code: 'Code',
    approve: 'Approve',
    deny: 'Deny',
    approved: 'Device approved',
    denied: 'Sign-in denied',
    decideError: 'The decision could not be saved',
    codeExpired: 'This code has expired',
    codeExpiredHint: 'Run elowen login on the device again.',
    codeDecided: 'This sign-in has already been decided',
    codeLoadError: 'The sign-in request could not be loaded',
    installTitle: 'Install',
    installHint: 'Run the copied command in a terminal on Linux or macOS.',
    installHintWindows: 'Paste the copied command into PowerShell on Windows.',
    installHintMac: 'Run the copied command in Terminal on macOS.',
    installHintLinux: 'Run the copied command in a terminal on Linux.',
    installerLabel: 'Operating system',
    installerUnix: 'macOS and Linux',
    installerWindows: 'Windows',
    installSelfOnly: 'Only the user themselves can connect a computer.',
    copyInstall: 'Copy install command',
    enrollmentLoadError: 'The install command lifetime could not be loaded',
    copyCountdown: 'Copied · {time}',
    copyAvailableAt: 'Install command copied. You can copy it again at {time}.',
    copied: 'Install command copied',
    installError: 'The install command could not be created',
    devicesTitle: 'My computers',
    connected: 'Connected',
    neverConnected: 'Not connected yet',
    computerName: 'Name',
    system: 'System',
    client: 'Client',
    emptyTitle: 'No computers yet',
    emptyDescription: 'Run the install command on a computer to connect it.',
    loadError: 'Your computers could not be loaded',
    offline: 'Offline',
    clientVersion: 'client {version}',
    lastOnline: 'Last online',
    statsLoadError: 'Usage could not be loaded',
    chartTitle: 'Turns and commands per day',
    chartTurns: 'Turns',
    chartCommands: 'Commands',
    chartErrors: 'Failed',
    chartCost: 'Cost',
    chartCostUnknown: 'Some turns in this window reported no cost, so their total is unknown rather than zero.',
    costUnknown: 'Cost unknown',
    totals: '{turns} turns · {commands} commands · {files} files edited · {tokens} tokens · {cost}',
    loadTitle: 'Load now',
    loadUnknown: 'No live reading. It appears while the computer is connected.',
    cores: { one: '{n} core', few: '{n} cores', many: '{n} cores' },
  },
  account: {
    effectsTitle: 'Visual effects',
    effectsHint: 'Controls animations and transitions on this device. Auto follows your operating system settings.',
    effectsAuto: 'Auto',
    effectsFull: 'Full range',
    effectsReduced: 'Reduced',
    effectsOff: 'Off',
    title: 'My account',
    navigationLabel: 'Account sections',
    navigationSearch: 'Search account',
    navigationNoMatches: 'No account setting matches this search.',
    profileHint: 'Manage your identity, defaults and connected accounts.',
    name: 'Name',
    email: 'Email',
    uploadAvatar: 'Upload avatar',
    saveError: 'Save failed',
    password: 'Password',
    passwordHint: 'Change your sign-in password. The new password must be at least 8 characters.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed',
    passwordMismatch: 'New passwords do not match',
    passwordWrong: 'Current password is incorrect',
    passwordError: 'Password change failed',
    uiScale: 'Interface scale',
    uiScaleReset: 'Reset',
    tabProfile: 'Account',
    tabApiTokens: 'API tokens',
    tabComputers: 'Computers',
    tabSecurity: 'Security',
    tabNotifications: 'Notifications',
    tabCli: 'Models',
    apiDocs: 'API docs',
    tabTerminal: 'Terminal',
    tabMemory: 'Memory',
    tabPersonality: 'Personality',
    personalPluginConfig: 'Personal plugin settings',
    platformLinkConflict: 'This identity is already linked to another account.',
    linkedAccounts: 'Linked accounts',
    linkDisconnect: 'Disconnect',
    detailsTitle: 'Personal details',
    detailsHint: 'How you appear across the workspace and where you receive email.',
    appearanceTitle: 'Appearance',
    appearanceHint: 'Display preferences for this device.',
    passwordRequirement: 'Use at least 8 characters.',
  },
  accountMemory: {
    recallTitle: 'Automatic memory search',
    recallToggle: 'Search memory after each message',
    liveRecallTitle: 'Search while preparing a reply',
    liveRecallToggle: 'Search for memories again while preparing a reply',
    saveTitle: 'Auto-save',
    saveToggle: 'Save new memories after each reply',
    learnSkillsTitle: 'Skill learning',
    learnSkillsToggle: 'Learn skills from completed work',
    saveError: 'Failed to save memory settings.',
    review: {
      last: 'Last {date}',
      none: 'No review yet',
      outcome: { nothing: 'nothing proposed', saved: 'saved {names}', rejected: 'proposals rejected', failed: 'failed', aborted: 'timed out' },
      totals: 'in total {parts}',
      runs: { one: '{n} review', few: '{n} reviews', many: '{n} reviews' },
      saved: { one: '{n} skill saved', few: '{n} skills saved', many: '{n} skills saved' },
      failed: { one: '{n} failed', few: '{n} failed', many: '{n} failed' },
      next: { one: 'next review in {n} step', few: 'next review in {n} steps', many: 'next review in {n} steps' },
    },
    dreaming: {
      abortCause: {
        disabled: 'Stopped after memory maintenance was disabled.',
        noProgress: 'Maintenance ended because the batch reviewed no additional memories.',
        user: 'Stopped by the user.',
        accountDeleted: 'Stopped because the account was deleted.',
        daemonPause: 'Interrupted while the service paused.',
        parentTeardown: 'Stopped when the parent conversation closed.',
        treeAbort: 'Stopped together with the parent task.',
        recovery: 'Stopped during service recovery.',
      },
      operationTitle: 'Memory maintenance',
      reviewing: 'Reviewed {reviewed} of {total} memories',
      showHistory: 'Show full history',
      hideHistory: 'Hide history',
      title: 'Automatic memory maintenance',
      hint: 'Merges similar notes, corrects outdated facts and shortens memories for later use.',
      toggle: 'Automatic memory maintenance',
      off: 'Nothing runs by itself while this is off.',
      model: 'Maintenance model',
      modelHint: 'Which model reconciles the memories. The default follows the model of the conversation the pass runs from.',
      sameModel: 'Same model as the conversation',
      unavailable: 'Unavailable',
      overview: 'Past maintenance passes',
      overviewHint: 'When each pass ran and what it changed. Open one to see the before and after of every memory it touched.',
      runNow: 'Run now',
      deleteRun: 'Remove from history',
      deleteTitle: 'Remove the maintenance record?',
      deleteBody: 'This record will be removed from maintenance history. Your memories and their change history will be preserved.',
      deleteError: 'Failed to remove the maintenance record.',
      stop: 'Stop',
      empty: 'No maintenance pass yet',
      emptyHint: 'The first pass starts on its own once the account has been quiet enough, or now if you start one.',
      noChanges: 'This pass changed no memory.',
      loadError: 'Failed to load memory maintenance.',
      runsError: 'Failed to load the maintenance passes.',
      changesError: 'Failed to load what this pass changed.',
      openMemory: 'Open the memory register',
      before: 'Before',
      after: 'After',
      memory: 'Memory #{id}',
      older: 'Older',
      newer: 'Newer',
      trigger: { auto: 'automatic', manual: 'manual' },
      status: { running: 'running', completed: 'completed', failed: 'failed', aborted: 'stopped', interrupted: 'interrupted' },
      counts: '{added} added · {updated} updated · {merged} merged · {deleted} deleted · {reviewed}/{batch} reviewed',
      refusal: {
        model_not_allowed: 'The chosen model is not allowed for this account.',
        model_unavailable: 'The chosen model is no longer offered.',
        memory_tools_not_granted: 'This account may not write memories, so maintenance cannot run.',
        memory_writes_denied_by_rule: 'A permission rule blocks memory writes, so maintenance cannot run.',
        no_conversation: 'A pass runs from one of your conversations, and there is none to start from yet.',
        spend_limit: 'This account has reached its spending limit.',
      },
    },
  },
  personality: {
    intro: 'Set how Chetty should communicate and work with you.',
    styleLabel: 'Communication style',
    styleProfessional: 'Professional',
    styleFriendly: 'Friendly',
    styleConcise: 'Concise',
    styleDetailed: 'Detailed',
    bodyLabel: 'Agent instructions',
    bodyPlaceholder: 'Write your instructions for Chetty…',
    bodyHint: 'Your instructions for how Chetty should behave and work with you. Used everywhere.',
    bodyEdit: 'Edit instructions',
    bodyAdd: 'Add instructions',
    saveError: 'Failed to save agent instructions',
  },
  memory: {
    embeddingTestError: 'The embedding model test failed.',
    // List
    workspaceIntro: 'Facts, decisions and preferences Chetty can use in future conversations.',
    synchronized: 'Memory synchronized',
    metricDecisions: 'Decisions',
    metricFacts: 'Facts',
    searchPlaceholder: 'Search memories…',
    searchClear: 'Clear the search',
    // The accessible name of a row's open control. It carries an EXCERPT of the memory, never the whole
    // body — the body is the record, not a name.
    openRow: 'Open memory: {excerpt}',
    filterStatus: 'Status',
    filterKind: 'Kind',
    statusActive: 'Active',
    statusArchived: 'Archived',
    statusDeleted: 'Deleted',
    statusAll: 'All',
    allKinds: 'All kinds',
    empty: 'No memories yet.',
    emptyHint: 'Add a fact you want the assistant to remember, or let it record them as you work.',
    emptySearch: 'No memories match your search.',
    newMemory: 'New memory',
    detailTitle: 'Memory detail',
    // Detail fields
    fieldBody: 'Memory',
    fieldBodyPlaceholder: 'A durable, self-contained fact…',
    fieldKind: 'Kind',
    fieldKindPlaceholder: 'e.g. user, feedback, project, reference',
    fieldImportance: 'Importance',
    fieldVitality: 'Vitality',
    // Usage
    usage: 'Usage',
    useCount: 'Used {n}×',
    neverUsed: 'Never used',
    usedAt: 'Used',
    // Vitality over time (detail drawer chart)
    vitalityChart: 'Vitality over time',
    vitalitySeriesLabel: 'series',
    vitalitySeriesMeasured: 'measured',
    vitalitySeriesForecast: 'forecast',
    vitalityEvictOn: 'Moves to the trash on {date}',
    vitalityForecastHint: "The dashed line assumes the memory won't be recalled again.",
    vitalityNoHistory: 'This memory was used before recall history was recorded, so only the forecast is shown.',
    // Timestamps
    createdAt: 'Created',
    updatedAt: 'Updated',
    sourceLabel: 'Source',
    detailsHeading: 'Details',
    // Actions
    edit: 'Edit',
    delete: 'Delete',
    restore: 'Restore',
    merge: 'Merge',
    save: 'Save',
    done: 'Done',
    cancel: 'Cancel',
    create: 'Create',
    // Toasts
    bodyRequired: 'The memory text is required',
    // Delete confirm
    deleteConfirmTitle: 'Delete memory?',
    deleteConfirmBody: 'The memory moves to the trash, and the assistant stops recalling it. You can restore it later.',
    // Merge
    mergeTitle: 'Merge memories',
    mergeHint: 'Combine the selected memories into one new memory. The originals move to the trash.',
    mergeBodyLabel: 'Merged memory',
    mergeSelected: '{n} selected',
    mergeConfirm: 'Merge',
    mergeNeedsTwo: 'Select at least two memories to merge.',
    // Purge (hard-delete) & trash
    purge: 'Delete permanently',
    purgeConfirmTitle: 'Delete permanently?',
    purgeConfirmBody: 'This permanently removes the memory. It cannot be restored.',
    purgeConfirm: 'Delete permanently',
    emptyTrash: 'Empty trash',
    emptyTrashConfirmTitle: 'Empty trash?',
    emptyTrashConfirm: 'Permanently delete every memory in the trash. This cannot be undone.',
    // Card order (filter panel)
    sortLabel: 'Sort',
    sortDirection: 'Direction',
    sortDescending: 'Descending',
    sortAscending: 'Ascending',
    // Bulk selection
    selectPage: 'Select this page',
    selectRow: 'Select memory: {excerpt}',
    clearSelection: 'Clear selection',
    selectedCount: '{n} selected',
    bulkDelete: 'Delete selected',
    bulkDeleteConfirmTitle: 'Delete selected memories?',
    bulkDeleteConfirmBody: 'Move the selected memories to the trash ({n})? You can restore them later.',
    bulkRestore: 'Restore selected',
    // Audit trail
    auditHeading: 'History',
    auditEmpty: 'No history yet.',
    actionAdd: 'Added',
    actionUpdate: 'Updated',
    actionDelete: 'Deleted',
    actionRestore: 'Restored',
    actionMerge: 'Merged',
    eventModel: 'Model',
    // Owner-scoped maintenance
    maintenanceTitle: 'Memory maintenance',
    maintenanceIntro: 'Refresh search data or organize your memories. Operations run in the background and continue even if individual items fail.',
    maintenanceReindex: 'Rebuild the memory index',
    maintenanceReindexRow: 'Memory index',
    maintenanceReindexHint: 'Rebuild search data for all active memories in your account.',
    maintenanceRecategorize: 'Reorganize memories',
    maintenanceRecategorizeRow: 'Memory sorting',
    maintenanceRecategorizeHint: 'The categorization model will assign your memories to your categories.',
    maintenanceCategorizationUnconfigured: 'Set up a categorization model in Settings before organizing memories.',
    maintenanceRun: 'Run',
    maintenanceRunAll: 'Run on everything',
    maintenanceAll: 'Reorganize all memories',
    maintenanceAllConfirmTitle: 'Reorganize all memories?',
    maintenanceAllConfirmBody: 'The categorization model will review every active memory in your account. Manual changes made while it runs will be preserved.',
    maintenanceFailed: 'Memory maintenance failed.',
    maintenanceRunning: 'Running',
    maintenanceDone: 'Completed',
    maintenanceError: 'Failed',
    maintenanceInterrupted: 'Interrupted by a restart',
    maintenanceInterruptedChip: 'Interrupted',
    maintenanceCounts: 'Succeeded: {succeeded}; failed: {failed}.',
    maintenanceAcknowledge: 'Confirm result',
    maintenanceIdle: 'Ready',
    maintenanceFailures: 'Items not updated: {n}',
    // Embedding settings section
    embeddingProvider: 'Embedding provider',
    embeddingProviderPlaceholder: 'e.g. openai',
    embeddingModel: 'Embedding model',
    embeddingDimensions: 'Dimensions',
    embeddingUnconfigured: 'Not configured',
    embeddingSaveError: 'Saving the embedding settings failed',
    embeddingTest: 'Test',
    embeddingTesting: 'Testing…',
    embeddingTestOk: 'Test succeeded — {dimensions} dimensions.',
    embeddingUnconfiguredError: 'Configure an embedding provider first.',
    reindexUnconfigured: 'Configure an embedding provider before reindexing.',
    // Categories
    categoryFilter: 'Category',
    categoryAll: 'All categories',
    categoryUncategorized: 'Uncategorized',
    categoryChipNone: 'No category',
    groupByCategory: 'Group by category',
    categoriesTitle: 'Categories',
    categoriesEmpty: 'No categories yet.',
    categoryNew: 'New category',
    categoryName: 'Name',
    categoryNamePlaceholder: 'e.g. Preferences',
    categoryDescription: 'Description',
    categoryDescriptionPlaceholder: 'What belongs in this category…',
    categoryProject: 'Project scope',
    categoryProjectGlobal: 'Global (all conversations)',
    categoryProjectsLoading: 'Loading projects…',
    categoryProjectsError: 'Projects could not be loaded.',
    categoryColor: 'Color',
    categoryIcon: 'Icon',
    categoryIconSuggest: 'Suggest an icon',
    categoryCreate: 'Create',
    categoryDelete: 'Delete category',
    categoryDeleteConfirmTitle: 'Delete category?',
    categoryDeleteConfirmBody: 'The category will be removed from all memories that use it. The memories themselves will remain.',
    categoryNameRequired: 'The category name is required',
    categoryNameExists: 'A category with this name already exists.',
    categorySaveError: 'Saving the category failed',
    memoryCount: '{n}',
    sharedBadge: 'shared · {project}',
    sharedBy: 'Shared by {author}',
    categoryShared: 'shared',
    categorySharedHint: 'This is shared project memory. Its description guides categorization for the whole team. Only an administrator can change it, and its project cannot be changed.',
    // View tabs
    viewBrain: 'Map',
    viewList: 'List',
    // Neural memory brain
    brainCore: 'Memory',
    brainEmpty: 'Nothing to map yet.',
    brainEmptyHint: 'As memories accumulate, their map will appear here.',
    brainCategoryCount: '{n} memories',
  },
  pills: {
    showMore: "+{n} more",
    showLess: "Show less",
  },
  categorization: {
    configured: "Configured",
    notConfigured: "Not configured",
    saveError: "Could not save the categorization settings.",
  },
  brain: {
    routerChoice: 'Router',
    routerRemoveMember: 'Remove from router',
    routerRequirement: 'Select at least two accounts from at least two providers.',
    routerEmpty: 'The selected accounts have no common models.',
    routerPreviewError: 'The common models could not be loaded.',
    routerPriceUnknown: 'Price unknown',
    routerUnknown: 'unknown',
    routerMemberLimits: 'Edit limits and prices on the member provider models.',
    routerLimits: 'Context: {context}, output: {output}',
    routerPrices: 'USD per million tokens: input {input}, output {output}',
    routerReferenced: 'A router uses this account. Edit its members before removing it.',
    maxSteps: "Max steps",
    maxStepsHint: "Maximum model rounds per request. Prevents runaway loops. Range 100–1,000 in steps of 100; default 200.",
    subagents: {
      forkParentContext: "Share context",
      forkParentContextHint: "Unless a Delegate call says otherwise, a sub-agent starts with this conversation: the same system prompt, the same tools and the full history. The provider can then reuse the cached prefix at a lower cost than fresh input. The cache is shared only when the sub-agent's access is not limited. That means it cannot have its own tools, read-only mode or sub-agent type. A different model does not share the cache.",
    },
    limits: {
      title: "Limits and connection retries",
      manage: "Edit limits",
      hint: "Assistant limits and the shared retry policy for model requests and connections.",
      toolOutputMaxLines: "Output: lines",
      toolOutputMaxLinesHint: "How many lines of a tool's output the expanded view shows before older lines are hidden. Range {min}-{max}, default {default}.",
      toolOutputMaxChars: "Output: tokens",
      toolOutputMaxCharsHint: "Approximate token cap on the expanded tool-output view; longer output is clipped. Calculated at 4 characters per token. Range ≈{min}–{max} tokens, default ≈{default}.",
      toolResultInlineBytes: "Inline limit",
      toolResultInlineBytesHint: "The inline limit for tool results and for Delegate and Workflow results. Above it a result arrives as a bounded preview only; the complete content stays available through Read. The value is in bytes. Range 30,000–180,000 B; default 120,000 B.",
      toolResultGroupBudgetBytes: "Limit per turn",
      toolResultGroupBudgetBytesHint: "Total limit for parallel tool results and completed delegated results in one turn. The largest results spill to disk until the block fits. The value is in bytes and never falls below the inline limit.",
      compactionFailureLimit: "Failures",
      compactionFailureLimitHint: "How many failures in a row before a conversation stops attempting automatic context compaction. Every further attempt would spend a model call that cannot succeed. Too low and a brief provider outage stops compaction for good; a manual \"/compact\" always stays available. Range 1-10, default 3.",
      retryMaxRetries: "Retry attempts",
      retryMaxRetriesHint: "Automatic retries after the first attempt for eligible transient model/request failures. The first attempt does not count. 0 means one attempt without automatic repeats. Paired computers and platform connections reconnect without a limit, with the same timing. Range {min}-{max}, default {default}.",
      retryBaseDelayMs: "Retry delay",
      retryBaseDelayMsHint: "Delay before the first retry, in seconds, shared by model/request retries and connection recovery. Later delays double up to 60 s, without random variation. A server-requested minimum delay can be longer. Range {min}-{max} s, default {default}.",
      compactionRetries: "Retries",
      compactionRetriesHint: "How many times the request that shortens the context is sent again after a transient error or a refused answer before the built-in summarization runs instead. 0 sends it once with no repeat. Range {min}-{max}, default {default}.",
      elicitationTimeoutMs: "Question wait",
      elicitationTimeoutMsHint: "How long a parked AskUserQuestion waits before resolving as \"no answer\". After it expires, the question cannot be answered in any chat or channel. Range 0.5 min to 6 h; default 6 h.",
      memoryRecallCount: "Recalled",
      memoryRecallCountHint: "How many relevant memories are injected into each turn's context. More = richer context but higher token cost. Range 1-20, default 10.",
      memoryRecallBytes: "Recall limit",
      memoryRecallBytesHint: "Approximate token budget for the memories injected at the start of a turn; recall stops packing once it is reached. The budget is counted in UTF-8 bytes, the same unit as recall while working, and the display estimates tokens at roughly 4 bytes each. Range ≈1,000–10,000 tokens, default ≈5,000.",
      memoryRecallWaitMs: 'Recall wait',
      memoryRecallWaitMsHint: 'Maximum time the first response waits for memory recall at the start of a turn. If search takes longer, the response starts without it and adds the results at the next step, or with the next message. Set 0 to never wait. Range {min}–{max} s, default {default} s.',
      memoryLiveRecallPasses: "Live searches",
      memoryLiveRecallPassesHint: "Safety cap on background memory searches in one turn, that is on recall while working. It prevents a long tool loop with no useful memory from issuing embeddings forever; set 0 to switch recall while working off. Range 0-20, default 10.",
      memoryLiveRecallCount: "Batch size",
      memoryLiveRecallCountHint: "The most memories one background retrieval may add at once, that is one batch of recall while working. Smaller batches keep each step focused; set 0 to switch recall while working off. Range 0-10, default 2.",
      memoryLiveRecallBytes: "Live budget",
      memoryLiveRecallBytesHint: "Total byte budget all mid-turn memory batches share in one turn, that is all recall while working together. Voice calls also have a whole-call memory allowance; the display estimates tokens at roughly 4 bytes each. Range ≈2,500–10,000 tokens, default ≈5,000.",
      voiceMemoryBytes: 'Voice budget',
      voiceMemoryBytesHint: 'Total memory allowance for one voice call, including startup and live recall. Delegating work to the assistant does not reset it. Set 0 to disable memory in calls. Stored as bytes including framing; the display estimates 4 bytes per token. Range ≈{min}–{max} tokens, default ≈{default}.',
      goalTurnBudget: "Turn budget",
      goalTurnBudgetHint: "Default number of autonomous turns a /goal runs before it pauses for you to confirm (or, in YOLO, continues). Range {min}-{max}, default {default}.",
      goalMaxTurns: "Safety ceiling",
      goalMaxTurnsHint: "Absolute cap on autonomous goal turns — even in YOLO the loop pauses here so a runaway goal can't burn tokens forever. Range {min}-{max}, default {default}.",
      channelSessionCap: "Live sessions",
      channelSessionCapHint: "How many live channel conversations (e.g. Discord threads) stay in memory before the least-recently-used one is dropped (its history stays in the database). Range 4-256, default 32.",
      clamped: "Saved as {value} — the value you set was outside the allowed range.",
      filterPlaceholder: 'Filter limits',
      noMatches: 'Nothing matches this filter',
      groupToolOutput: 'Tool output and results',
      groupCompaction: 'Context compaction',
      groupRetries: 'Retries and waiting',
      groupMemory: 'Memory',
      groupGoals: 'Goals',
      groupChannels: 'Channels',
      groupSubagents: 'Sub-agents',
      minuteUnit: "min",
      secondUnit: "s",
      tokenUnit: "tokens",
    },
    runtime: {
      title: "Runtime",
      manage: "Edit runtime",
      hint: 'Runtime settings: local command line, memory relevance, deferred tools, log retention and chat connection checks. Every value is limited to a sensible range. Keep the defaults unless you have a reason to change them.',
      localShellTimeoutMs: 'Shell timeout',
      localShellTimeoutMsHint: "How long a command started from the CLI with \"!\" may run before it is killed. It runs on the machine the CLI runs on, not among the assistant's tools. Range 10-300 s, default 30 s.",
      codeModeDefaultYieldTimeMs: 'Exec wait',
      codeModeDefaultYieldTimeMsHint: "How long exec holds without its own pragma before returning a partial result. The wait command has its own wait time. A higher value saves model rounds for long-running scripts. Range 10-600 s, default 10 s.",
      codeModeMaxOutputTokens: 'Max. output',
      codeModeMaxOutputTokensHint: "Code mode: hard cap on text output in every exec and wait report. It also limits explicit max_output_tokens and max_tokens, keeping a script from flooding the context. Range 1,000-50,000 tokens, default 10,000.",
      memorySemanticFloorPerMille: 'Relevance',
      memorySemanticFloorPerMilleHint: "Relevance floor: the minimum semantic similarity at which a memory counts as related to the query; anything below is left out of recall and of the memory search box. Higher = stricter, fewer memories recalled. Genuinely related pairs usually land above 0.50. Range {min}-{max}, default {default}.",
      memoryDuplicatePerMille: 'Save dupes',
      memoryDuplicatePerMilleHint: "Duplicate threshold when saving: at this similarity, automatic saving treats a new memory as a rewording and replaces the stored one. A memory saved by the assistant is kept and only reported as similar. A lower value limits duplicates but raises the risk of overwriting different information. Recheck the threshold after changing the embedding model or growing memory substantially. Range 0.50–0.98; default 0.93.",
      memoryParaphrasePerMille: 'Recall dupes',
      memoryParaphrasePerMilleHint: "Duplicate threshold when recalling: the similarity at which an already-picked memory makes another one redundant, so it is left out of the prompt. Stops two write-ups of one fact from taking two of the few available slots. A mistake here only drops one result, so it can be looser than the saving threshold. Range 0.50-0.98, default 0.70.",
      memoryImportanceWeightPerMille: 'Importance',
      memoryImportanceWeightPerMilleHint: "Importance weight in recall: how much of the ranking comes from the importance you set by hand. Whatever these two values leave goes to matching the query, so raising both lowers the influence of the question itself. Range 0-30%, default 10%.",
      memoryVitalityWeightPerMille: 'Vitality',
      memoryVitalityWeightPerMilleHint: "Vitality weight in recall: how much of the ranking comes from vitality — how often and how recently a memory was used. Note that vitality grows from recall itself, so a high value creates a loop where the same memories keep winning and the rest of the store never gets a turn. Range 0-30%, default 10%.",
      memoryCuratorMaxOps: 'Memory writes',
      memoryCuratorMaxOpsHint: "How many memories automatic saving may add, revise or delete after a single exchange. 0 switches automatic saving off entirely; the manual memory tools keep working. Higher fills the store faster than you can review it. Range 0-6, default 2.",
      memoryDreamingMinHours: 'Dreaming hours',
      memoryDreamingMinHoursHint: "Memory dreaming, hours since the last pass. How long the account must have gone without a maintenance pass before the next one may start automatically. A pass reconciles memories across conversations, so a shorter interval means more passes and a higher cost. Range {min}-{max} h, default {default}.",
      memoryDreamingMinSessions: 'Dreaming chats',
      memoryDreamingMinSessionsHint: "Memory dreaming, conversations since the last pass. How many of the account's OTHER conversations must each hold a finished exchange since the last pass before the next one may start automatically. One busy conversation is not yet a sign that stored memories contradict each other. Range {min}-{max}, default {default}.",
      skillReviewMaxOps: 'Skill writes',
      skillReviewMaxOpsHint: "How many skill writes (creating or improving a skill) one review may propose. It limits proposed writes, not model calls. 0 switches skill learning off for everyone; the manual skill tools keep working. Skills a person wrote or edited are never changed. Range {min}-{max}, default {default}.",
      skillReviewMaxCorrections: 'JSON fixes',
      skillReviewMaxCorrectionsHint: "Additional model calls in the same review when its JSON cannot be parsed or its proposal envelope has the wrong shape. Does not retry invalid individual proposals or provider failures. 0 disables corrections. Each extra call is billed normally. Range {min}-{max}, default {default}.",
      skillReviewEveryToolCalls: 'Every N calls',
      skillReviewEveryToolCallsHint: "Skill review every N tool calls. Counted across exchanges: once an account's completed, eligible exchanges add up to this many successful tool calls, the completed conversation context is reviewed. Lower finds more procedures and costs more model calls. Range {min}-{max}, default {default}.",
      skillReviewMinToolCalls: 'Minimum calls',
      skillReviewMinToolCallsHint: "Minimum tool calls for a skill review. Counted within one completed exchange: an exchange with fewer successful tool calls is too simple to hold a procedure, so it is neither reviewed nor counted toward the review interval. Raise it to learn only from longer tasks. Range {min}-{max}, default {default}.",
      eventRetentionDays: 'Activity log',
      eventRetentionDaysHint: "How long entries in the activity timeline are kept before the hourly cleanup removes them. Range 1-365 days, default 30 days.",
      providerRequestRetentionDays: 'Diagnostics',
      providerRequestRetentionDaysHint: "How long exact provider request bodies remain available for incident diagnosis. Cleanup removes a whole diagnostic session, never part of a request, and leaves the conversation untouched. Range 1-90 days, default 14 days.",
      providerRequestRetentionMiB: 'Storage',
      providerRequestRetentionMiBHint: "The global logical storage budget for new-format exact provider request diagnostics. Above it, cleanup removes the oldest complete diagnostic sessions while preserving conversations and every request that remains; legacy captures are governed by the age limit above. Range 64-16384 MiB, default 1024 MiB.",
      originIpRetentionDays: 'IP addresses',
      originIpRetentionDaysHint: "How long a recorded client IP stays readable in the usage-by-origin view before the hourly cleanup replaces it with a placeholder. The token and cost totals survive; only the address goes. The row itself is removed later, on the activity log retention above. Range 1-365 days, default 30 days.",
      streamSilenceLimitMs: 'Silence limit',
      streamSilenceLimitMsHint: "How long the chat stream may go without a single frame — a message or the daemon's heartbeat — on a page you are watching, before the browser treats the connection as dead and reconnects. The lower end stops at 35 s on purpose: the heartbeat arrives every 30 s, so a shorter limit would declare a healthy but idle stream dead. Range 35-300 s, default 75 s.",
      streamReviveSilenceLimitMs: 'Wake-up limit',
      streamReviveSilenceLimitMsHint: "The same silence, measured the moment the page wakes up (screen unlock, returning to the tab). A sleeping page runs no timers, so the check above cannot have run and the stream is judged against the clock instead — usually a little more strictly. Same 35 s floor as the limit above, for the same reason. Range 35-300 s, default 45 s.",
      toastDurationMs: 'Notification',
      toastDurationMsHint: "How long a notification in the top-right corner of the web app stays on screen before it dismisses itself. Hovering over it pauses the countdown and it can always be closed by hand, so this only sets how long it waits on its own. Range 2-15 s, default 4.5 s.",
      stepContextEveryToolCalls: 'Reminders',
      stepContextEveryToolCallsHint: 'Mid-turn reminder interval. How often plugin reminders are added during a turn. Ordinary providers count tool calls within a turn. Task tracking counts assistant messages since both its last task change and its last reminder. Parallel calls count as one assistant message and thinking-only messages do not count. Existing saved values are kept. Reminders append to the conversation without rewriting its cached prefix. Disabling the contributing plugin switches its reminders off.',
      planModeExploreAgents: 'Explore agents',
      planModeExploreAgentsHint: "Explore agents: the largest number of read-only explore sub-agents Plan mode suggests running in parallel while the assistant studies the code. The assistant decides how many it actually starts, usually one. 0 leaves the exploring to the assistant itself. Applies only where sub-agents are available. Range {min}-{max}, default {default}.",
      planModePlanAgents: 'Plan agents',
      planModePlanAgentsHint: "Plan agents: the largest number of read-only plan sub-agents Plan mode suggests running in parallel to design the change, each from its own angle. The assistant decides how many it actually starts and writes the final plan itself. 0 leaves the design to the assistant alone. Applies only where sub-agents are available. Range {min}-{max}, default {default}.",
      subagentRunnerEnabled: 'Own processes',
      subagentRunnerEnabledHint: "Delegated turns run in separate processes, so the system spreads work across cores and the interface stays responsive. Off runs them inside the daemon. Takes effect immediately without a restart.",
      clamped: 'Saved as {value}: the value was outside the allowed range.',
      filterPlaceholder: 'Filter settings',
      noMatches: 'Nothing matches this filter',
      groupExecution: 'Command execution',
      groupMemory: 'Memory',
      groupSkills: 'Skills',
      groupPlanning: 'Sub-agents',
      groupChat: 'Chat and notifications',
      groupRetention: 'Retention',
      secondUnit: "s",
      dayUnit: { one: "day", few: "days", many: "days" },
      tokenUnit: "tokens",
    },
    toolLoading: {
      title: "Tool loading",
      manage: "Manage tools",
      hint: "Choose which tool definitions stay in the prompt in full; the rest are loaded on demand. Changes apply to new and recreated conversations.",
      globalTitle: "Tool loading policy",
      globalHint: "Keep tool definitions out of the prompt until they are needed. Local ToolSearch searches names, descriptions and parameters by words and, when an embedding model is configured, by meaning. Existing conversations keep their current tool set.",
      enabled: "Deferred loading",
      enabledHint: "Off keeps every tool definition in the prompt. Your per-tool choices stay saved and take effect again when this is turned back on.",
      threshold: "Threshold",
      thresholdHint: "Tools without an explicit choice, except the core tools, are loaded on demand when their count exceeds this value. Range 1-100, default 10.",
      search: "Search tools",
      searchPlaceholder: "Search tools or plugins",
      clearSearch: "Clear search",
      resultCount: {
        one: "{n} tool",
        few: "{n} tools",
        many: "{n} tools",
      },
      default: "Default",
      immediate: "Immediate",
      deferred: "On demand",
      groupMode: "Loading mode for {group}",
      toolMode: "Loading mode for {tool}",
      plugin: "Plugin",
      builtIn: "Built-in",
      empty: "No matching tools.",
      loadError: "The tool catalog could not be loaded.",
      saveError: "Tool loading settings could not be saved.",
      reason: {
        'global-disabled': "Global setting is off",
        core: "Core tool",
        'tool-override': "Tool choice",
        'source-override': "Plugin choice",
        'source-default': "Default choice",
        threshold: "Automatic rule",
      },
    },
    retention: {
      title: "Memory retention",
      manage: "Edit retention",
      hint: 'How long an unused memory keeps its value before it is moved to the trash. The move can be undone. Every importance level fades eventually, the higher the level the slower. The daily limit sets how many memories can be moved automatically in 24 hours.',
      enabled: 'Automatic',
      enabledHint: "The master switch. Off = no memory is ever moved to the trash automatically, whatever the values below say. On by default.",
      graceDays: 'Grace period',
      graceDaysHint: 'New memories are not removed during this period, even if unused. They still have a chance to be recalled. Range 0–365 days, default 14.',
      vitalityFloor: 'Vitality floor',
      vitalityFloorHint: 'An active memory whose value falls below this threshold is moved to the trash. The move can be undone. A higher value means more thorough cleanup. Range 1–90, default 10.',
      dailyDeletionLimit: 'Daily limit',
      dailyDeletionLimitHint: 'How many of one account\'s memories can be moved to the trash automatically in any 24 hours. The rest wait for a later cleanup. Range 1–200, default 5.',
      groupCleanup: 'Memory cleanup',
      halfLifeSection: 'Half-life',
      halfLifeRow: 'Importance {n}',
      halfLifeLevel: 'Time to halve, importance {n}',
      halfLifeHint: "How many days it takes for an unused memory's value to halve. The lower the value, the faster the memory fades and reaches the threshold. Range 0.1–365 days, defaults 1/3/7/14/21/30/45/60/90/180.",
    },
    contextWindow: "Context",
    contextWindowHint: "How much conversation and prompt the model accepts.",
    modelLimits: "Model limits",
    modelLimitsEdit: "Edit model limits",
    modelLimitOutOfBounds: "The value must be a number from {min} to {max}.",
    modelOverrideInherited: "Inherited",
    modelVision: "Images",
    modelVisionHint: "Whether the model can read image turns.",
    modelVisionYes: "Yes",
    modelVisionNo: "No",
    modelPrice: "Price / 1M tokens (USD)",
    modelPriceInput: "Input",
    modelPriceOutput: "Output",
    modelPriceCacheRead: "Cache read",
    modelPriceCacheWrite: "Cache write",
    modelPriceHelp: "Provider or catalog rate. An actual per-request charge reported by the provider takes precedence.",
    modelImageResize: "Image resizing",
    modelImageWidth: "Max width",
    modelImageWidthHint: "The largest image width in pixels.",
    modelImageHeight: "Max height",
    modelImageHeightHint: "The largest image height in pixels.",
    modelImageBytes: "Max size",
    modelImageBytesHint: "The largest image size once encoded.",
    modelImageQuality: "JPEG quality",
    modelImageQualityHint: "JPEG quality when an image is resized.",
    modelImageResizeHelp: "Images are resized before being stored in the conversation. Previously stored images are unchanged.",
    maxOutputTokens: "Max output",
    maxOutputTokensHint: "How much the model may write in one reply. It cannot exceed the context.",
    maxOutputTokensTooLarge: "This context window allows at most {max} output tokens.",
    modelsHintPicker: "Loaded from the API address. Click to choose models. If you don't choose any, all will be used.",
    pickModelsTitle: "Models - {provider}",
    pickModelsHint: "Choose which models Chetty can offer from this account. If you don't choose any, only text models are offered. Select other kinds explicitly.",
    modelsUnavailable: 'No longer in the catalog',
    filterModelsByKind: 'Filter models by kind',
    modelOutputKinds: { text: 'Text', embeddings: 'Embeddings', image: 'Image', audio: 'Audio', video: 'Video', transcription: 'Transcription', speech: 'Speech', decisions: 'Decisions', rerank: 'Rerank' },
    imageModelDefault: 'Provider default',
    addAccount: 'Add account',
    account: 'Account',
    noAccounts: 'No account is linked yet.',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    relogin: 'Sign in again',
    deleteAccount: 'Delete account',
    deleteAccountConfirm: 'Delete the account {account} and its stored configuration? Its login and preferences are removed, unlike pausing, which keeps both.',
    kindsError: 'Could not load the supported services. Retry to add a provider.',
    spendLimitAction: 'Spend limit',
    accountCount: { one: '{n} account', few: '{n} accounts', many: '{n} accounts' },
    accountUsage: 'Account usage',
    settings: 'Settings',
    detailSections: 'Provider settings sections',
    tabGeneral: 'General',
    tabConnection: 'Connection',
    tabAdvanced: 'Advanced',
    connected: 'Connected',
    accountActive: 'Active',
    providerId: 'Provider ID',
    providerIdHint: 'The id that model references, grants and role pins name. It is chosen once, when the provider is created, and never changes.',
    advancedNone: 'This provider has no advanced settings.',
    routing: {
      title: 'Routing',
      on: 'Routing on',
      off: 'Routing off',
      enabled: 'Choose the account automatically',
      enabledHint: 'Off: every conversation uses the default account and nothing switches on its own. On: the account with the least consumption is chosen, a running conversation keeps its account, and an exhausted one fails over to another eligible account.',
      defaultAccount: 'Default account',
      defaultAccountHint: 'The account routing-off uses, and one candidate among the others while routing is on. It passes exactly the same checks as any other account, so a paused or unauthorized default leaves the provider unavailable instead of routing around it.',
      noDefault: 'No default account',
      idleRebalance: 'Move idle conversations',
      idleRebalanceHint: 'Move an idle conversation to a less-used account. Measured from its last model request; a long-running tool or stream is not idle.',
      idleMinutes: 'Move after inactivity',
      idleMinutesHint: 'How long a conversation must be idle before it may move. Judged at the next request, never by a wake-up during the idle time.',
      minGain: 'Minimum gain',
      minGainHint: 'The smallest improvement in the busiest quota window that justifies a voluntary move, so small fluctuations do not move conversations.',
      minutes: '{n} min',
      hours: '{n} h',
      days: '{n} d',
      points: '{n} pp',
    },
    usageWeekly: 'weekly',
    usageWindow: 'window',
    usageResets: 'Resets {time}',
    usageResetsUtc: 'Free models, daily allowance, resets {time} (00:00 UTC)',
    usageDaily: 'daily',
    usageMonthly: 'monthly',
    usageBalance: 'Balance',
    usageBalanceHint: 'Balance: {amount}',
    usageLimit: 'Limit',
    usageKeyLimitHint: 'Key limit {amount}',
    usageFreeModels: 'Free',
    usageSpend: 'Spend',
    accounts: 'Linked accounts',
    providers: 'AI providers',
    addProvider: 'Add provider',
    removeProvider: 'Remove provider',
    removeProviderConfirm: 'Remove {provider} and its stored configuration? This cannot be undone.',
    providerLabel: 'Name',
    baseUrl: 'API address',
    baseUrlHintOpenai: 'Include the version segment, e.g. https://ai.example.com/v1',
    baseUrlHintAnthropic: 'Leave empty for https://api.anthropic.com',
    probeFailed: 'The endpoint did not answer: {reason}',
    apiKey: 'API key',
    apiKeyKeepHint: 'Leave empty to keep the stored key.',
    wireApi: 'Wire API',
    wireApiMessages: 'Anthropic',
    modelApi: 'API',
    modelApiReset: 'Reset API',
    modelApiParameterReset: 'Reset detection for {parameter}',
    modelApiUnavailable: 'API status is unavailable.',
    temperature: 'Temperature',
    temperatureInvalid: 'Enter a number from 0 to 2, or leave the field empty to use the model default.',
    temperatureHint: 'Controls how much the model varies its wording. Lower means steadier answers, higher means more inventive ones. Leave the field empty to send no value and let the model use its default. This setting applies only to this provider because some models reject other values. Kimi K3 and Claude Opus 4.7 and newer accept only their default.',
    compatibility: {
      title: 'Compatibility',
      safeHint: 'Auto inherits the model settings. Change values only according to the provider documentation.',
      safeDefaults: 'Automatic settings',
      customSummary: '{n} custom setting(s)',
      on: 'On',
      off: 'Off',
      temperatureOverride: 'Custom temperature',
      reset: 'Reset to automatic settings',
      supportsLongCacheRetention: 'Cache 24 h',
      supportsLongCacheRetentionHint: 'Sends prompt_cache_retention and a stable cache key. Unsupported endpoints may reject the whole request.',
      supportsDeveloperRole: 'Developer role',
      supportsDeveloperRoleHint: 'Uses the developer role for model instructions. Leave this off for relays that accept only the classic system role.',
      supportsUsageInStreaming: 'Usage',
      supportsUsageInStreamingHint: 'Requests token totals in the final streamed event through stream_options.',
      supportsStrictMode: 'Strict schemas',
      supportsStrictModeHint: 'Marks function schemas as strict. Enable only when the endpoint supports OpenAI strict mode.',
      supportsStore: 'Storage',
      supportsStoreHint: 'Sends store: false. Some compatible endpoints do not recognise this OpenAI-specific field.',
      supportsReasoningEffort: 'Reasoning',
      supportsReasoningEffortHint: 'Sends the selected level as reasoning_effort when the model supports reasoning.',
      maxTokensField: 'Token limit',
      maxTokensFieldHint: 'Choose the request field this endpoint uses to limit the response length.',
    },
    wireApiAuto: 'Auto',
    wireApiResponses: 'Responses',
    wireApiCompletions: 'Chat Completions',
    models: 'Models',
    modelsHintAuto: 'Enter one model ID per line. Leave the list empty to load models from this endpoint’s /models automatically.',
    modelCount: 'Models: {n}',
    modelsAuto: 'Models listed automatically',
    keySet: 'Key is set',
    hostedSearchUnsupported: 'Tool search unsupported',
    hostedSearchUnverified: 'Tool search not verified',
    codeModeTitle: 'Code mode',
    codeModeHelp: 'The model gets a single `exec` tool taking raw JavaScript and reaches every other tool from inside the script, so dependent calls resolve in one turn instead of one model round each. Every call still passes the same permissions and approvals a direct call would. It applies to every model of this provider. Models trained on this surface (GPT-5.6 and newer) drive it best; on any other model, compare a real task before leaving it on.',
    responsesStatefulTitle: 'Stateful responses',
    responsesStatefulHelp: 'The provider stores responses on its servers and later requests continue from the previous response. This may improve cache reuse. Retention is set by the provider.',
    hostedSearchTitle: 'Built-in tool search',
    hostedSearchHelp: 'The provider searches its own tool catalogue, so the full catalogue is not sent in the prompt. When off, this provider uses local ToolSearch, which matches words in names, descriptions and parameters. Each local search costs one extra model round. Native search also requires “Defer external tools” to be on.',
    toolStatusError: 'Could not load tool execution status for the providers below.',
    testProvider: 'Test connection',
    testOk: 'Test passed, model: {model}',
    testFailed: 'Test failed',
    testUnreachable: 'The test could not be run. Check that the Chetty service is reachable and try again.',
    saveError: 'Saving providers failed',
    providerSaveError: 'Could not save the provider. Check the endpoint address and credentials, then try again.',
    noProviders: 'No providers are set up yet. Add one so the assistant can use it.',
    notConnected: 'Not connected',
    connect: 'Connect',
    disconnect: 'Disconnect',
    disconnectConfirm: 'Disconnect {provider}? Chetty will stop using this account until it is connected again.',
    connectedToast: 'Account connected',
    connectFailed: 'Connecting the account failed',
    connectError: 'Could not start sign-in.',
    connectUnknownAccount: 'This account is no longer in the configuration. Close this dialog and add the account again.',
    connectInProgress: 'A sign-in for this account is already in progress. Finish or close that one, then try again.',
    connectTitle: 'Connect account',
    connectStarting: 'Starting sign-in…',
    connectUserCode: 'Enter this code',
    connectCodePlaceholder: 'Paste the code or redirect address',
    connectMethod: 'Sign-in method',
    connectBrowser: 'Browser',
    connectCopyCode: 'Copy code',
    connectCopyCodeHint: 'Open the authorization link, sign in, then paste the code shown by Claude.',
    connectSubmitError: 'Could not submit the code. Please try again.',
    connectCodeError: 'Sign-in failed. The code may be incorrect or expired. Close this dialog and start sign-in again.',
    connectSubmitCode: 'Submit',
    connectWaiting: 'Waiting for sign-in to finish…',
    keyAccount: {
      minimaxKeyHint: 'Paste the Subscription Key from your international MiniMax account (M Plan), not a pay-as-you-go key or a China account key. It is stored on this server and never shown again.',
      connectTitle: 'Connect {provider}',
      editTitle: 'Connection: {provider}',
      editConnection: 'Edit connection',
      accountLabel: 'Account name',
      accountLabelHint: 'How this account is listed. Renaming it moves no id and touches no history.',
      endpointHint: 'The endpoint the catalog fixes for this service.',
      keyHint: 'Paste the API key from your {provider} account. It is stored on this server and never shown again.',
      format: 'API format',
      formatHint: 'Auto selects an API per model. Selecting a format pins the API for the whole provider.',
    },
    types: {
      'openai': 'OpenAI-compatible',
      'anthropic': 'Anthropic API',
      'oauth-anthropic': 'Claude account',
      'oauth-openai-codex': 'ChatGPT account',
      'oauth-github-copilot': 'GitHub Copilot',
      'oauth-kimi': 'Kimi account',
      'oauth-xai': 'xAI account',
      'oauth-meta': 'Meta account',
      'oauth-opendesign': 'Opendesign account',
      'oauth-openrouter': 'Openrouter account',
    },
    stats: {
      button: "Statistics",
      trackedSince: "Tracked since {day}",
      refreshError: "Statistics could not be refreshed. The figures below may be out of date.",
      rangeHelp: "Days are counted in UTC, and a period always covers whole days.",
      kpiTokens: "Tokens",
      kpiCost: "List price",
      kpiRequests: "Requests",
      kpiCache: "Cache hits",
      kpiCacheHelp: "The share of input that the provider read from its cache instead of receiving it again.",
      costHelpSubscription: "The list price is what the same tokens would cost at the per-token prices Chetty knows for these models. Accounts on a subscription are not billed per token, so this is an equivalent, not a charge. A model with no known price counts as $0.00.",
      costHelpComputed: "The list price is computed from the model prices Chetty knows, so it may differ from your invoice. A model with no known price, or a free one, counts as $0.00.",
      costHelpReported: "OpenRouter reports the cost of every request, so this is the amount it charged, not an estimate.",
      costUnavailable: "Not available",
      allowanceTitle: "Allowance",
      allowanceNone: "No allowance reported",
      allowanceNoneHelp: "This provider does not report a usage limit that Chetty can read, so no percentage is shown.",
      usersTitle: "Who used it",
      usersHelp: "Each person's share of the tokens Chetty sent to this provider in the period, counted for whoever ordered the turn. It is not a share of the provider's allowance.",
      usersOthers: "Others",
      modelsTitle: "Models",
      trendTitle: "Per day",
      chartTokens: "Tokens",
      chartCost: "List price",
      chartsAriaLabel: "Tokens and list price per day",
      breakdownTitle: "Token breakdown",
      tokensIn: "Input",
      tokensOut: "Output",
      cacheRead: "Cache read",
      cacheWrite: "Cache write",
      reasoning: "Reasoning",
      breakdownHelp: "Reasoning tokens are already counted in output.",
      empty: "No usage in this period",
      emptyNever: "Nothing has been recorded for this provider yet",
      loadError: "Statistics could not be loaded",
      subscriptionTitle: "Subscription",
      subscriptionUnavailable: "The current windows could not be read right now.",
      quotaTitle: "Share of the subscription used per day",
      quotaHelp: "Providers report only the current window, so Chetty writes it down as it goes: earlier days cannot be recovered, use before the first reading and between the last reading and a reset is not seen, and days when Chetty was not running have no figure. The first recorded day therefore counts only from its first reading. The figures cover the whole account, including use outside Chetty.",
      quotaRecordingStarted: "Recording of the subscription has just begun. The chart fills in over the next days.",
      quotaEmptyRange: "Nothing was recorded in this period. Recording began on {day}.",
      quotaGapNote: "On some days readings were missing for hours, because Chetty was not running or the provider could not be reached. Use during that time is counted on the day it was next seen.",
      quotaSince: "Recorded since {day}",
      quotaTotal: "In this period",
      quotaTotalHelp: "The days in the chart added together. 100 % is one whole {window} window, so a longer period can exceed 100 %.",
      quotaBusiest: "Busiest day",
      quotaBusiestGap: "may include earlier days",
      quotaSeriesConsumed: "Used per day (window: {window})",
      quotaChartAria: "Share of the subscription used per day",
      tokensSectionTitle: "Tokens through Chetty",
      tokensSectionHelp: "The tokens Chetty sent to this provider. They are Chetty's own count and are not the same thing as the subscription percentages above, which cover the whole account.",
    },
  },
  plugins: {
    bundled: 'Bundled',
    user: 'User-installed',
    tools: 'Tools',
    skills: 'Skills',
    platforms: 'Platforms',
    enable: 'Enable',
    disable: 'Disable',
    pendingToast: 'Change saved. It takes effect the next time the daemon starts.',
    restartingToast: 'Change saved. Chetty is restarting now to load it.',
    grantsTitle: 'Enable {name}?',
    grantsIntro: 'This plugin asks for access that outlives a single reply. Enabling it grants:',
    grantsConfirm: 'Enable and grant',
    grantTools: 'Change the tools available to the agent, including replacing existing ones',
    grantControl: 'Use the {control} plugin interface',
    grantToolAuthority: 'Use the {authority} authority profile for tools matching {pattern}',
    grantManagedProjectRead: 'Read files in managed Projects',
    grantOwnOutputRead: 'Read stored outputs of the current conversation',
    grantProjectFileRead: 'Read Project files through the binary-data interface',
    grantProjectFileWrite: 'Write Project files through the binary-data interface',
    grantProjectTransfer: 'Transfer files between accessible Projects',
    grantSkillLoad: 'Load skills through tools matching {pattern}',
    grantServerAutomation: 'Run automated tasks on the server',
    grantTerminalTimeouts: 'Read terminal timeout settings',
    grantAgentDirectory: 'Read the sub-agent definitions directory',
    grantWorkflowEngine: 'Use the workflow engine',
    authorityGranted: 'Granted',
    authorityMissing: 'Consent missing',
    grantPrompt: 'Replace the system prompt and prompt templates',
    grantEvents: 'Write to the activity log',
    grantWorkflowDag: 'Change running sub-agent workflows',
    grantConversations: 'Open conversations in granted accounts and run the agent there at the account\'s cost',
    dependencyHelp: 'Works only with {plugins} enabled.',
    dependencyOn: 'Turn on {plugin} first. This add-on needs it for {control}.',
    dependencyMissing: 'This add-on needs another add-on that provides {control}, and none is installed.',
    dependencyInUse: 'This add-on provides {control}, which {plugin} still needs. Turn off {plugin} first.',
    toggleError: 'Changing the plugin failed',
    updateInProgress: 'An update is in progress. Please wait until it finishes before changing this plugin.',
    empty: 'No plugins found',
    searchPlaceholder: 'Search plugins…',
    categoryFilter: 'Category',
    catAll: 'All',
    catPlatforms: 'Platforms',
    catTools: 'Tools',
    catMemory: 'Memory',
    catAutomation: 'Automation',
    catUi: 'Interface',
    catSecurity: 'Security',
    catDevelopment: 'Development',
    healthOk: 'Healthy',
    healthError: 'Error',
    noMatches: 'No matching plugins',
    noMatchesHint: 'Try a different search term or category.',
    install: 'Install',
    installing: 'Installing…',
    installError: 'Plugin install failed',
    installQueued: 'Installing the plugin. Chetty will restart briefly in a moment, and the plugin will then be ready.',
    updateQueued: 'Updating the plugin. Chetty will restart briefly in a moment and then run the new version.',
    updateAllQueued: 'Updating the plugins. Chetty will restart briefly in a moment and then run the new versions.',
    repairQueued: 'Repairing the plugin. Chetty will restart briefly in a moment, and the plugin will then work again.',
    uninstall: 'Uninstall',
    uninstallConfirm: 'Really uninstall the “{name}” plugin? Its files and stored data will be removed.',
    uninstallError: 'Plugin uninstall failed',
    remove: 'Remove',
    removeConfirm: 'Remove the built-in “{name}” plugin? It will disappear from the list and stop loading. Its files will stay, and you can restore it anytime from Available plugins.',
    removedError: 'Plugin removal failed',
    restore: 'Restore',
    restoreError: 'Plugin restore failed',
    removedSection: 'Removed built-in plugins',
    updateAvailable: 'A newer version is available',
    updateError: 'Plugin update failed',
    updateAll: 'Update all plugins',
    updateAllConfirm: 'Update all plugins with a newer version available? They will update together and restart Chetty. Disabled plugins will stay disabled.',
    marketplaceEmpty: 'No additional plugins available yet',
    marketplaceError: 'Couldn’t load the plugin catalog',
    detail: 'Detail',
  },
  pluginDetail: {
    workspaceNav: 'Plugin workspace',
    tabSetup: 'Setup',
    tabBehavior: 'Behavior',
    tabCapabilities: 'Capabilities',
    tabActivity: 'Activity',
    tabAdvanced: 'Advanced',
    setupChecklist: 'Setup checklist',
    setupChecklistHint: 'Required values needed before this plugin is ready.',
    setupMissing: '{n} missing',
    setupNoRequired: 'This plugin has no required setup steps.',
    statusTitle: 'Status',
    statusHint: 'What this plugin needs to work. Checks run continuously.',
    statusOk: 'Everything works',
    statusProblem: '{n} needs attention',
    statusCopy: 'Copy',
    overviewVersion: 'Version',
    overviewSource: 'Source',
    statusDisabled: 'Disabled',
    config: 'Configuration',
    behaviorEmpty: 'This plugin has no additional behavior settings.',
    configEmpty: 'This plugin has no configurable settings.',
    tools: 'Tools',
    toolsHint: 'Tools this plugin adds to the assistant.',
    toolsEmpty: 'This plugin registers no tools.',
    skills: 'Skills',
    platforms: 'Platforms',
    hooks: 'Hooks',
    hooksHint: 'Hooks this plugin registers while the assistant is running.',
    hooksEmpty: 'This plugin registers no hooks.',
    hookExecutions: 'Recent executions',
    hookExecutionsHint: 'Each time this plugin’s hook ran, its outcome and duration.',
    outcomeOk: 'Applied',
    outcomeThrew: 'Failed',
    outcomeTimeout: 'Timed out',
    outcomeRejected: 'Rejected',
    permissions: 'Permissions',
    capabilities: 'Declared capabilities',
    capabilitiesDeny: 'Deny by default: the plugin can only do what it declares here. A missing capability means it cannot perform that action.',
    capMutates: 'May modify',
    capNetwork: 'Network',
    capNetworkOn: 'Outbound access',
    capReads: 'Reads',
    capAuthorities: 'Authority consents',
    permissionsHint: 'What this plugin needs and how sensitive it is.',
    requiresEnv: 'Required environment variables',
    requiresConfig: 'Required configuration',
    requiresNone: 'This plugin requires no special environment or configuration.',
    riskLow: 'Low risk',
    riskMedium: 'Medium risk',
    riskHigh: 'High risk',
    data: 'Stored data',
    dataPath: 'Location',
    dataSize: 'Size',
    dataFiles: '{n} files',
    dataPartial: 'Partial measurement: the scan reached its limit, so the totals shown are the lowest possible.',
    dataUnreadable: '{n} entries could not be measured. An environment disk belongs to its machine, and this service cannot read it.',
    dataEmpty: 'This plugin has not stored any data.',
    dataClear: 'Clear data',
    dataClearConfirm: 'Delete all stored data for this plugin? This cannot be undone.',
    dataClearError: 'Clearing the plugin data failed',
    logs: 'Logs',
    logsHint: 'The most recent log lines from this plugin.',
    logsEmpty: 'No recent log entries.',
  },
  agents: {
    tokens: 'Tokens',
    /** A DelegateContinue whose message entered the sub-agent's already RUNNING turn: it ran nothing and
     *  finished nothing, so its row says where the message went instead of reporting a completed run. */
    steeredInto: 'received new instructions while working',
    steerQueued: 'Awaiting delivery',
    steerDelivered: 'Instruction delivered',
    steerDropped: 'Instruction not delivered',
  },
  brainChat: {
    skillLabel: 'Skill',
    archiveWaiting: 'A response is waiting. Answer it before closing.',
    archiveFailed: 'The conversation could not be archived.',
    archiveConflict: 'The conversation changed. Reload it before trying again.',
    compacting: 'Compacting conversation…',
    ux: {
      recalledInputsLabel: 'Returned messages',
      recalledInputsCount: 'Waiting to restore: {count}',
      restoreRecalledInput: 'Restore message',
      messageCopy: "Copy message",
      codeCopy: "Copy code",
      regenerate: "Regenerate",
      editResend: "Edit and resend",
      editMessage: "Edit message",
      reissueConfirm: "Run this exchange again?",
      reissueWarning: "The original reply will be replaced. Completed actions are not undone and tools may run again.",
      reissueUnconfirmed: "Submission is unconfirmed. Refresh the conversation state first.",
      attachmentsKept: "Original attachments will be kept.",
      approvalConversation: "Allow in this conversation",
      approvalConversationHint: "Allow “{pattern}” for this account and execution target in this conversation, including after a restart.",
      approvalInstead: "No, instead…",
      approvalInstruction: "What should the model do instead?",
      approvalSubmitInstead: "Deny and send instruction",
      approvalFeedbackRequired: "Enter an instruction between 1 and 2000 characters.",
      approvalConversationOutcome: "Allowed in this conversation",
      approvalFeedbackOutcome: "Denied with an instruction",
      revokeConsents: "Revoke approvals",
      revokeConsentsHint: "Only your saved approvals in this conversation will be revoked.",
      consentsRevoked: "Approvals for this conversation were revoked.",
      deliveryNow: "Send now",
      deliveryChanged: "Sent — Chetty will read it at its very next step.",
      deliveryUnavailable: "This message can no longer be sent now. The queue has been refreshed.",
      searchTitle: "Search this conversation",
      searchPlaceholder: "Search text (at least 2 characters)…",
      searchEmpty: "No matching messages.",
      searchFailed: "Could not search messages.",
      searchNext: "Next match",
      searchPrevious: "Previous match",
      searchMore: "Load more matches",
      searchCount: "Loaded matches: {count}",
      searchLatest: "Back to latest",
      shortcutsTitle: "Keyboard shortcuts",
      shortcutsCommands: "Commands",
      shortcutsScope: "Shortcuts apply only while working in chat. Browser keys stay unchanged outside chat.",
      shortcutChange: "Change",
      shortcutCapture: "Press keys…",
      shortcutUnbound: "Unbound",
      shortcutInvalid: "Invalid shortcut notation.",
      shortcutConflict: "This shortcut is used by “{action}”.",
      shortcutReserved: "This combination cannot be used in web chat.",
      shortcutSaveError: "Could not save shortcuts.",
      replyAction: "Reply",
      cancelReply: "Cancel reply",
      typingOne: "{name} is typing…",
      typingMany: "Typing: {names}",
      answeringUser: "Answering: {name}",
      unreadDivider: "New messages",
      jumpToUnread: "Jump to new messages",
      muteConversation: "Mute notifications",
      unmuteConversation: "Unmute notifications",
      conversationMuted: "Conversation notifications are muted.",
      conversationUnmuted: "Conversation notifications are unmuted.",
      conversationMuteFailed: "Notification settings could not be saved.",
      globalReplyMuteHint: "Reply notifications are still muted in account settings.",
      shortcutActions: {
        leader: "Shortcut leader",
        mode_toggle: "Switch mode",
        reasoning_cycle: "Reasoning level",
        stash: "Stash draft",
        rewind: "Rewind message",
        rename: "Rename conversation",
        subagent_cycle: "Cycle agents",
        subagent_stop: "Stop agent",
        telemetry_toggle: "Statistics",
        queue_steer: "Send queued now",
        queue_remove: "Remove queued",
        stop: "Stop run",
        reconnect: "Reconnect",
        help: "Open help",
        theme_picker: "Change appearance",
        model_picker: "Choose model",
        sessions_picker: "Choose conversation",
        search: "Search conversation",
      },
    },
    promptHistoryError: 'Prompt history could not be read or saved in this browser.',
    leaderPending: 'Ctrl+X: waiting for the next key',
    suggestionHint: 'Tab or Space inserts the suggestion; Enter sends the message.',
    viewLabel: 'Interactive view',
    viewLoading: 'Loading view…',
    viewError: 'The answer could not be confirmed. Please try the choice again.',
    historyLoadError: 'Conversations could not be loaded.',
    loadingOlder: 'Loading older messages',
    olderLoadError: 'Older messages could not be loaded.',
    attach: 'Attach a file',
    attachRemove: 'Remove attachment',
    attachmentAlt: 'Attached image',
    attachmentOpen: 'Open full size',
    attachmentGone: 'Image no longer available',
    imageAttached: 'Image in the conversation',
    messageBlocks: 'Items mentioned in the reply',
    fileDownload: 'Download',
    attachFailed: 'The file could not be uploaded and was not attached.',
    uploadProgress: 'Uploading {name}',
    uploadPercent: '{percent}%',
    uploadCancel: 'Cancel uploading {name}',
    attachedFile: 'attached file',
    attachOnly: '(attachment)',
    deleteChat: 'Delete conversation',
    deleteChatConfirmTitle: 'Delete this conversation?',
    deleteChatConfirmDescription: 'This permanently deletes “{title}” and its message history. If it is currently open, chat switches to another conversation. This cannot be undone.',
    deleteChatError: 'The conversation could not be deleted. Nothing was changed.',
    bulkDelete: 'Delete selected',
    bulkDeleteConfirmTitle: 'Delete selected conversations?',
    bulkDeleteConfirmBody: 'This permanently deletes {n} conversations and their message history. If the currently open conversation is among them, chat switches to another one. This cannot be undone.',
    bulkDeletePartial: '{done} conversations deleted, {failed} could not be deleted.',
    bulkDeleteFailed: 'The selected conversations could not be deleted. Nothing was changed.',
    selectedCount: '{n} selected',
    clearSelection: 'Clear selection',
    selectRow: 'Select conversation: {title}',
    context: 'context',
    newChat: 'New chat',
    searchPlaceholder: 'Search conversations…',
    searchError: 'The search could not be completed',
    searchOpenError: 'This conversation cannot be opened',
    newChatError: "Couldn't start a new conversation.",
    abortError: "Couldn't stop the reply.",
    conversationBusy: 'The conversation is waiting for another action to finish. Please try sending your message later.',
    sendError: "Message couldn't be sent. Your draft is still in the editor.",
    sendErrorPreviousConversation: 'The message to the previous conversation was not sent. Its text is waiting in that conversation\'s draft.',
    freshConversation: 'The previous conversation was inactive. Continuing in a new one.',
    readOnly: 'This conversation takes place on another platform or in a task. Here you can only follow along.',
    readOnlyExit: 'Back to chat',
    childFocusCommandDisabled: 'This command belongs to the parent conversation — leave the focused sub-agent first.',
    untitled: 'Untitled',
    placeholder: 'Write a message, or enter / for commands',
    placeholderShort: 'Message…',
    send: 'Send',
    stop: 'Stop',
    rewindHelp: 'F9: stop the reply, then remove the latest messages',
    rewindEmpty: 'No message left to remove.',
    rewindActionsRemain: 'Conversation history removed; actions already performed were not undone.',
    rewindFailed: 'Could not change conversation history.',
    clearCompacting: 'The conversation is being compacted. Try again once it finishes.',
    clearSettling: 'Work is running in this conversation. Stop it or wait for it to finish.',
    clearQueued: 'A message is queued. Remove it or wait for it to be sent.',
    clearPendingQuestion: 'Answer or dismiss the pending question first.',
    clearDelegationRunning: 'A sub-agent or workflow is running. Stop it or wait for it to finish.',
    clearProcessRunning: 'A background process is running. Stop it in the process panel.',
    clearActiveGoal: 'Pause or clear the active goal first.',
    clearResultPending: 'A delegated result is waiting to be delivered. Try again after it arrives.',
    rewindCompacting: 'The conversation is being compacted. Try again once it finishes.',
    rewindSettling: 'The reply is still being saved. Try again in a moment.',
    rewindQueued: 'A queued message is waiting. Wait for it to be sent, then try again.',
    rewindPendingQuestion: 'Answer or dismiss the pending question first.',
    rewindActiveGoal: 'Pause or clear the active goal first.',
    rewindUnsettledTurn: 'The last turn has not finished yet. Try again in a moment.',
    rewindSubagentRunning: 'This reply started a sub-agent that is still running. Stop it or wait for it to finish first.',
    rewindWorkflowRunning: 'This reply started a workflow that is still running. Stop it or wait for it to finish first.',
    rewindResultPending: 'The result of work started by this reply has not been delivered yet. Try again once it arrives.',
    modelSwitchQueued: 'The model switches after the current answer.',
    modelPicker: 'Model',
    modelPickerLoading: 'Loading models…',
    modelPickerEmpty: 'No model available',
    modelPickerError: 'Failed to load models',
    modelPickerRetry: 'Retry',
    projectPickerFailed: "Couldn't change the execution target.",
    projectPickerHost: 'No project',
    newConversationProject: {
      title: 'Where should this conversation run?',
      empty: 'No project is available for this account.',
    },
    modelActive: 'Active model',
    modelReasoning: 'Reasoning effort',
    askWaiting: 'Chetty is waiting for your answers',
    approvalWaiting: 'Approval needed',
    approvalRunCommand: 'Run this command?',
    approvalRunTool: 'Allow the "{tool}" tool to run?',
    approvalOnce: 'Allow once',
    approvalAlways: 'Always allow',
    approvalAlwaysHint: 'always allow "{pattern}"',
    approvalDeny: 'Deny',
    // Informational note on a shell approval prompt, keyed on the daemon's DestructiveWarningId. It never
    // changes what the rules decide — see src/brain/destructiveCommandWarning.ts.
    approvalWarningNote: 'Note: this command {note}.',
    approvalWarnings: {
      gitResetHard: 'may discard uncommitted changes',
      gitForcePush: 'may overwrite remote history',
      gitCleanForce: 'may permanently delete untracked files',
      gitDiscardWorktree: 'may discard working tree changes',
      gitStashDrop: 'may permanently remove stashed changes',
      gitBranchForceDelete: 'may force-delete a branch',
      gitNoVerify: 'may skip safety hooks',
      gitCommitAmend: 'may rewrite the last commit',
      rmRecursiveForce: 'may recursively force-remove files',
      rmRecursive: 'may recursively remove files',
      rmForce: 'may force-remove files',
      sqlDropTable: 'may drop or truncate database objects',
      sqlDeleteAll: 'may delete all rows from a database table',
      kubectlDelete: 'may delete Kubernetes resources',
      terraformDestroy: 'may destroy Terraform infrastructure',
      mkfs: 'may erase a filesystem',
      deviceOverwrite: 'may overwrite a raw disk device',
      chmodWorldWritable: 'may make files world-writable',
      killEveryProcess: 'may kill every process of this account',
      forkBomb: 'may exhaust the process table',
    },
    askOther: 'Custom answer',
    askOtherPlaceholder: 'Type your own answer',
    askSubmit: 'Send answer',
    askRecommended: 'Recommended',
    askMulti: 'select one or more',
    askPreview: 'Preview',
    askDetailBody: 'Full text',
    askProgress: '{answered} of {total} answered',
    askSending: 'Sending…',
    askRetry: 'Try again',
    askReadOnly: 'Answer this question in the main conversation.',
    askExpired: 'This question is no longer available to answer.',
    askExpiredRecord: 'Question expired without an answer.',
    askAllowedOnce: 'Allowed once',
    askAllowedAlways: 'Always allowed',
    askDenied: 'Denied',
    askQuestionOf: 'Question {current} of {total}',
    askError: 'Could not send the answer. Try again.',
    toolOutputExpand: 'Show full output',
    toolOutputCollapse: 'Show less',
    diffLabel: 'Change diff',
    diffExpand: 'Show the remaining {n} lines of the diff',
    diffCollapse: 'Show fewer lines of the diff',
    diffRowAdded: 'added line',
    diffRowRemoved: 'removed line',
    cardFallback: 'Card',
    eventModel: 'model',
    eventWireApi: 'The model now communicates through {api}.',
    // The conversation's OWN account line: the account serving it, and why it moved. The daemon bounds it —
    // a foreign or revoked previous account arrives without a label — so nothing here can name one.
    accountMoved: 'moved: {reason}',
    accountMovedFrom: 'from {account}: {reason}',
    accountChangeReason: {
      paused: 'the account is paused',
      'not-authorized': 'the account is not authorized for this conversation',
      'no-credential': 'the account has no credential',
      'model-unavailable': 'the model is not available there',
      'quota-limited': 'its quota is exhausted',
      'spend-limit': 'its monthly spend limit applies',
      'already-attempted': 'it was already used',
      regenerated: 'its sign-in was renewed',
      reconfigured: 'the account was reconfigured',
      rebalanced: 'the load was rebalanced',
    },
    eventMode: 'mode',
    eventMembershipJoined: '{name} joined the conversation',
    eventMembershipLeft: '{name} left the conversation',
    eventMembershipRemoved: '{name} was removed by {actor}',
    eventMembershipRemovedAnonymous: '{name} was removed from the conversation',
    eventPlanDismissed: 'Plan dismissed',
    eventPlanHeldComputer: 'The plan stayed on computer {name} and is not available in this turn. It moves here once the transfer can finish.',
    eventPlanHeldProject: 'The plan stayed in Project {name} and is not available in this turn. It moves here once the transfer can finish.',
    eventPlanHeldServer: 'The plan stayed on the Chetty server and is not available in this turn. It moves here once the transfer can finish.',
    eventRenamed: 'renamed',
    eventCwd: 'cwd',
    eventOfflineTarget: 'Computer {computer} is offline. This conversation now runs on {project}.',
    eventSubagentDone: 'sub-agent finished working',
    eventSubagentFailed: 'sub-agent failed',
    eventWorkflowDone: 'workflow completed',
    eventWorkflowFailed: 'workflow failed',
    eventWorkflowStopped: 'workflow stopped',
    eventDreaming: { one: 'Dreaming improved {n} memory', few: 'Dreaming improved {n} memories', many: 'Dreaming improved {n} memories' },
    eventNodes: 'nodes',
    eventReasoning: 'reasoning',
    eventInterruptedUser: 'Interrupted by user',
    eventInterruptedTool: 'Interrupted by user while a tool was running',
    eventInterruptedStepLimit: 'Stopped: the turn reached its limit of {n} steps',
    eventInterruptedParent: 'Stopped by the delegating agent',
    eventError: 'Error',
    eventErrorProviderRetries: 'The provider request failed after automatic retries. Please try again.',
    eventErrorCompactionStopped: 'Automatic context compaction failed {n} times in a row, so it has been stopped for this conversation. Start a new conversation, or run /compact yourself once the oversized content is out of the way.',
    eventErrorCompactionUnreachable: 'Automatic context compaction cannot shrink this conversation below its threshold: after compacting, the context still measures about {floor} tokens, at or above the {trigger}-token trigger. Automatic compaction has been stopped here; /compact still works, and a new conversation is the reliable fix.',
    nativeCheckpointConversionFailed: 'The conversation could not be converted to readable history. Sign in to the original ChatGPT account, ensure the original model is permitted, and retry the change.',
    clientSessionStopped: 'Your message was not sent because this window was replaced or stopped. Please send it again.',
    localTargetOffline: 'The computer for this conversation is offline. The Chetty connection on that computer is not running; start it there and try again.',
    localWorkDirMissing: 'The working directory of this conversation no longer exists on its computer. Restore the directory, or start a new conversation in an existing one.',
    eventErrorModelSwitchFailed: 'Model switch failed: {error}',
    commandCompacted: 'Conversation compacted',
    commandNotCompactedAlready: 'Not compacted: already compacted, nothing new since the last summary',
    commandNotCompactedNothing: 'Not compacted: nothing to compact yet',
    commandGoalSet: 'Goal set: {goal}',
    commandGoalDraft: 'Goal draft saved: {goal}',
    commandGoalPaused: 'Goal paused',
    commandGoalResumed: 'Goal resumed',
    commandGoalCleared: 'Goal cleared',
    commandGoalNone: 'No active goal',
    commandGoalStatus: 'Goal {status} · {used}/{budget} {turns}',
    commandGoalSubgoals: 'Subgoals {done}/{total}',
    commandGoalPausedReason: 'Paused: {reason}',
    commandGoalEvidence: 'Evidence: {evidence}',
    goalState: { active: 'active', draft: 'draft', paused: 'paused', done: 'done' },
    commandFastOn: 'Fast mode on for this model',
    commandFastOff: 'Fast mode off for this model',
    commandFastUnavailable: 'Fast mode is not available for this model',
    commandRestart: 'Restarting the Chetty daemon…',
    shellExit: 'exit {code}',
    shellKilledTimeout: 'stopped: time limit reached',
    shellKilledAbort: 'stopped',
    shellKilledOverflow: 'stopped: output too large',
    shellOutput: 'Command output',
    commandFailedRestartUnavailable: 'Restart is not available on this deployment',
    commandFailedNotRunning: 'The conversation is not running yet. Send a message first, then try again.',
    contextCompacted: 'Context shortened',
    proposedPlan: 'Proposed plan',
    planImplement: 'Implement plan',
    planDecision: 'The plan is waiting for your decision. You can also keep refining it with messages.',
    planRefine: 'Keep refining',
    planDecisionError: 'The plan decision could not be confirmed. Please try again.',
    modeSwitchError: 'The conversation mode could not be changed. Please try again.',
    statusReadError: 'The conversation status could not be loaded.',
    workMode: { build: 'Build', plan: 'Plan', workflow: 'Workflow' },
    actions: 'Options',
    actionsTitle: 'Options, mode: {mode}',
    workModeGroup: 'Work mode',
    /** One-line hints under the command rows of the mode switch and the mascot menu. The daemon catalog
     *  describes commands in English only; these are the reader's-language twins, keyed by slash name. */
    commandHints: {
      plan: 'Think the approach through before editing',
      build: 'Implement changes with tools',
      workflow: 'Orchestrate the task as a graph of sub-agents',
      compact: 'Summarize the conversation to free up space in the context',
      rename: 'Rename this conversation',
      new: 'Start a fresh conversation',
      model: 'Switch the AI model',
    },
    /** A picker command a plugin published that this build has no renderer for. A refusal, not a result:
     *  nothing opened, and the reader is told where the command does work. */
    renameTitle: 'Rename conversation',
    commandField: {
      open: 'Open the command field',
      title: 'Command field',
      compact: 'Compact',
      rename: 'Rename',
      newChat: 'New conversation',
      model: 'Model',
    },
    queued: 'Queued',
    removeFromQueue: 'Remove from queue',
    queueRemoveError: 'Could not remove the message. It is still queued for delivery.',
    queueDetachCapacity: 'The background process limit has been reached.',
    queueDetachUnavailable: 'The running work cannot be moved to the background.',
    toolRunning: 'Running',
    reasoningLabel: 'Reasoning',
    turnDuration: 'Run time',
    workLive: 'Working…',
    workDuration: 'Worked for {duration}',
    workLog: 'Work log',
    workStopped: 'Stopped',
    workStoppedAfter: 'Stopped after {duration}',
    workFailed: 'Failed',
    workFailedAfter: 'Failed after {duration}',
  },
  chat: {
    historyTitle: 'Conversations',
    openHistory: 'All conversations',
    openRegister: 'All conversations',
    registerHint: 'A complete overview of conversations from the web, CLI, channels, and agent tasks.',
    moreOptions: 'More options',
    showStats: 'Show stats',
    hideStats: 'Hide stats',
    todos: 'Tasks',
    emptyHistory: 'No conversations yet',
    rename: 'Rename',
    renamePlaceholder: 'Conversation title',
    renameError: 'Could not rename the conversation',
    conversationNamePlaceholder: 'Automatic title',
    pin: 'Pin conversation',
    unpin: 'Unpin conversation',
    pinError: 'Could not update the conversation pin',
    fork: 'Branch conversation',
    forkError: 'Could not branch the conversation',
    exportHtml: 'Export as HTML',
    exportJsonl: 'Export as JSONL',
    exportError: 'Export failed',
    moreActions: 'More actions',
    roleYou: 'You',
    roleElowen: 'Chetty',
    activityIdle: 'No recent activity',
    activityWorking: 'Working',
    activityPermission: 'Waiting for approval',
    activityQuestion: 'Waiting for an answer',
    activityBackground: 'Working in the background',
    activityDone: 'Completed',
    activityDoneScheduled: 'Scheduled job completed',
    activityFailed: 'Run failed',
    activityUnread: 'Unread result',
    participants: 'Members',
    membersCount: 'Members {count}',
    inviteHeading: 'Invite…',
    removeMemberLabel: 'Remove from conversation: {name}',
    participantsLoadFailed: 'The participants could not be loaded.',
    participantChangeFailed: 'The participants could not be changed.',
    inviteConfirm: 'Invite',
    inviteConfirmTitle: 'Invite to this conversation?',
    inviteConfirmBody: 'They will see the whole history.',
    invited: '{name} was invited.',
    alreadyParticipant: 'Already in the conversation',
    noInvitable: 'There is nobody else to invite.',
    invitableLoadFailed: 'The people you can invite could not be loaded.',
    clearFilter: 'Clear the filter',
    noPeopleMatch: 'Nobody matches the filter.',
    inviteFailed: {
      notShareable: 'This conversation cannot be shared.',
      generic: 'The invitation could not be sent.',
    },
    leaveConversation: 'Leave conversation',
    leaveConfirm: 'Leave this conversation?',
    leaveConfirmBody: 'Your manual invitation will be removed. Access granted by a plugin will remain.',
    removedFromConversation: 'You were removed from this conversation.',
    conversationUnavailable: 'The conversation is not available.',
    ownerOnlyControl: 'Only the conversation owner can change this.',
    ownerDecidesPlan: 'The conversation owner decides the plan.',
    queuedBy: 'Queued by {name}',
    replyTo: 'Reply to',
    jumpToMessage: 'Jump to the original message',
    messageUnavailable: 'The message is no longer available.',
    react: 'React',
    reactionUp: 'Thumbs up',
    reactionDown: 'Thumbs down',
    reactedBy: 'Reacted: {names}',
    reactionFailed: 'The reaction could not be saved.',
    mentionPlaceholder: 'Mention someone',
    mentionOwnerOnly: 'Only the conversation owner can invite.',
    mentionNotParticipantError: 'The mentioned account is no longer in this conversation.',
    mentionsYou: 'Mentions you',
    mentionChip: 'Mention:',
    mentionAgentHint: 'Answers this message',
    ownerBadge: 'Owner',
    agentGroup: 'Shared conversation',
    pauseAgent: 'Pause the agent',
    pauseAgentHint: 'People keep talking, the agent stays quiet',
    resumeAgent: 'Resume the agent',
    resumeAgentHint: 'It answers the next message and sees everything',
    agentPaused: 'The agent is paused. It will see messages once resumed, or right away when you mention @Chetty.',
    agentPauseFailed: 'The agent could not be paused or resumed.',
  },
  telemetry: {
    title: 'Telemetry',
    contextUsed: 'Used',
    contextTokens: 'Tokens',
    cost: 'Cost',
    resize: 'Telemetry panel width',
    open: 'Show telemetry',
    close: 'Hide telemetry',
    collapse: 'Collapse telemetry panel',
    expand: 'Expand telemetry panel',
    statusRunning: 'Working',
    statusIdle: 'Idle',
    empty: 'No telemetry available yet.',
    limits: 'Limits',
    goal: 'Goal',
    goalTurns: { one: 'turn', few: 'turns', many: 'turns' },
    goalSubgoals: 'Subgoals',
    goalProgress: 'Progress: {text}',
    tasks: 'Tasks',
    tasksOpen: 'Open the task list',
    taskWaiting: 'waiting on {ids}',
    workflow: 'Workflow',
    branch: 'branch',
  },
  workflowModal: {
    elapsed: 'Run elapsed',
    graph: 'Workflow node graph',
    wave: 'Wave {n}',
    gone: 'This workflow is no longer in the live view.',
    statusPending: 'waiting',
    statusRunning: 'running',
    statusDone: 'done',
    statusError: 'failed',
    deps: 'Depends on',
    depsNone: 'no dependencies',
    result: 'Result',
    error: 'Error',
    activity: 'Progress',
    working: 'Working…',
    failed: 'The node failed.',
    notStarted: 'The node has not started yet.',
    noOutput: 'No output was reported.',
    openConversation: 'Open conversation',
    openUnavailable: 'Available once the node starts.',
    followLive: 'Follow live',
    followLiveHint: 'Select a running node to resume following.',
    backToGraph: 'Graph',
    feeds: 'Feeds',
    task: 'Task',
    model: 'Model',    status: 'Status',
    reasoning: 'Reasoning',
    tokens: 'Tokens',
    speed: 'Speed',
    time: 'Time',
    waveCount: '{n} in parallel',
    finishedOf: '{done} of {total} finished',
    runStopped: 'Stopped',
    reconnecting: 'Live updates paused, reconnecting…',
    planBuilding: 'The plan is still being built.',
    keysMove: 'Arrows to move',
    keysOpen: 'Enter to open',
    keysClose: 'Esc to close',
    stop: 'Stop workflow',
    stopConfirm: 'Stop this workflow and its running nodes?',
    stopFailed: 'Could not stop the workflow.',
    terminalNode: '{id} {status}',
    resultFailed: 'The complete node result could not be loaded.',
    resultReleased: 'This is a shortened preview; the complete result is no longer in the live view. Open the node conversation to read it all.',

  },
  access: {
    statusConnected: 'Connected',
    statusMissing: 'Required details missing',
    statusReconnect: 'Reconnect needed',
    statusUnset: 'Not set',
    emptyTitle: 'No access to manage',
    emptyDescription: 'No enabled plugin needs a key, token or password.',
    reconnectHint: 'A stored value can no longer be read. Enter it again.',
    loadFailed: 'This plugin\'s access details could not be loaded.',
    cardDescription: 'This plugin\'s keys and passwords are managed in one place, Settings → Access.',
    open: 'Open in Access',
  },
  pluginCfg: {
    roleAdmin: "Administrator access",
    roleAdminHint: "Marks a trusted platform administrator role. Account projects and tool permissions still come from the linked Chetty account.",
    roleAdminBadge: "Admin",
    sectionConnection: 'Connection & secrets',
    sectionConnectionHint: 'Tokens, IDs and endpoints. Stored on the server and never shown again.',
    sectionBehavior: 'Behavior',
    back: 'Plugins',
    invalidJson: 'Enter valid JSON before this change can be saved.',
    numberRange: 'Allowed range: {min}–{max}.',
    // Summaries a field whose editor opens in a modal shows in its settings row: how much is stored,
    // never the content itself.
    editorEmpty: 'Empty',
    editorLines: '{n} lines',
    editorItems: '{n} entries',
    secretSet: 'Stored',
    secretUnreadable: 'Unreadable, enter it again',
    secretKeepHint: 'The stored value remains unchanged until you replace it. Replacing it with an empty value deletes it.',
    secretReplace: 'Replace',
    secretReplaceHint: 'Saves when you leave the field or press Enter. An empty field deletes the stored value; Esc cancels the replacement.',
    secretPlaceholder: 'Enter a value',
    secretReplacementPlaceholder: 'Enter a replacement',
    noProviders: 'No suitable provider is available. Add one in Settings → Chetty AI.',
    catalogProjects: 'Projects',
    catalogPlugins: 'Plugins you can grant',
    timezoneServerDefault: 'Server default',
    timezoneBrowserDetected: 'Browser detected',
    timezoneCatalog: 'Timezones',
    timezoneSaved: 'Saved value',
    timezoneCustom: 'Custom IANA timezone',
    timezoneCustomPlaceholder: 'e.g. Pacific/Kiritimati',
    userMissing: 'Account not found',
    usersLoadFailed: 'Couldn\'t load accounts',
    tokenListAdd: 'Add to {label}',
    tokenListItems: '{label} entries',
    tokenListRemove: 'Remove {value}',
    tokenListBrowse: 'Browse directories',
    tokenListPlaceholder: 'Value, then Enter',
    noRoles: 'No roles mapped yet',
    noRolesHint: 'Users without a mapped role are ignored.',
    roleNew: 'New role',
    roleId: 'Role ID',
    roleName: 'Name',
    rolePrompt: 'Role prompt',
    addRole: 'Add role',
    removeRole: 'Remove role',
    removingRole: 'Removing…',
    removeRoleTitle: 'Remove role policy “{name}”?',
    removeRoleDescription: 'Saving this policy changes platform permissions for users with the “{name}” role. They may lose access or administrator privileges.',
    valueRefused: 'The server refused this value. Check what you entered and try again.',
    roleActivationPending: 'The role policy was saved. Permission activation is pending while current work finishes.',
  },
  terminal: {
    saveError: 'Failed to save terminal settings.',
    fontTitle: 'Font',
    fontSize: 'Size',
    fontFamily: 'Font family',
    fonts: { system: 'System', menlo: 'Menlo / Consolas', ibm: 'IBM Plex Mono', courier: 'Courier' },
    cursorTitle: 'Cursor',
    cursorStyle: 'Style',
    cursorBlink: 'Blink the cursor',
    cliTitle: 'CLI chat',
    showThoughts: 'Show reasoning (Thought) rows in the CLI',
    showThoughtsHelp: "The model's reasoning lines in the conversation transcript. Quick toggle: /reasoning show.",
    promptHistoryDepth: 'Prompt history',
    promptHistoryDepthHelp: 'How many sent prompts chat keeps for recall with the up arrow. CLI history is per project on this computer; web history is per account in this browser across conversations. The selected depth travels with your account. Range 20–1,000, default 100.',
    lineUnit: 'lines',
    cursorBlock: 'Block',
    cursorBar: 'Bar',
    cursorUnderline: 'Underline',
    historyTitle: 'Scrollback',
    scrollback: 'Lines',
    scrollbackHelp: 'How many lines of history the terminal keeps.',
    colorsTitle: 'Colors',
    colorsHelp: 'Auto uses the dark terminal palette. Custom uses your palette.',
    themeMode: 'Color theme',
    themeAuto: 'Auto',
    themeCustom: 'Custom',
    loadPreset: 'Load preset',
    presetPlaceholder: 'Choose a preset…',
    palette: {
      background: 'Background', foreground: 'Text', cursor: 'Cursor', cursorAccent: 'Cursor highlight', selectionBackground: 'Selection',
      black: 'Black', red: 'Red', green: 'Green', yellow: 'Yellow', blue: 'Blue', magenta: 'Magenta', cyan: 'Cyan', white: 'White',
      brightBlack: 'Bright black', brightRed: 'Bright red', brightGreen: 'Bright green', brightYellow: 'Bright yellow', brightBlue: 'Bright blue', brightMagenta: 'Bright magenta', brightCyan: 'Bright cyan', brightWhite: 'Bright white',
    },
  },
  cli: {
    // Account → Models. The shared role vocabulary (Model roles, Inherit, Inherited) is read from
    // `settings.modelRoles`, so both pages say the same words for the same thing.
    modelRolesHint: 'Which model your own conversations use.',
    chatRuntimeTitle: 'Chat runtime',
    promptSuggestions: 'Next-message suggestions',
    promptSuggestionsHint: 'After a reply, suggest a next message in the web and CLI. Uses an additional paid request to the next-message suggestion model; without an explicit pick that is the utility model.',
    chatRuntimeHint: 'How a conversation behaves — none of these choose a model.',
    // A stored pick the catalog no longer offers. The runtime does not honour it — the spawn chain
    // skips a refused selection and compaction discards a stale one — so the row names what runs.
    unavailableBadge: 'Unavailable',
    unavailableSummary: '{model} is unavailable. Using {fallback}.',
    unavailableNoFallback: 'no fallback',
    projectPinFallback: 'This project uses fallback model {fallback}.',
    inheritUnknown: 'Inherit',
    unreadableTitle: 'Some saved settings cannot be read',
    unreadableDesc: 'These saved settings are damaged and are shown as empty: {names}. Changes to them are refused until you reset them.',
    unreadableReset: 'Reset unreadable settings',
    unreadableResetTitle: 'Reset the unreadable settings?',
    unreadableResetDesc: 'The damaged values are removed and cannot be recovered. Your choices for them start from empty.',
    unreadableResetConfirm: 'Reset',
    unreadableResetPending: 'Resetting',
    unreadableKeys: {
      autoCompactAtByModel: 'Compaction thresholds per model',
      projectModelPreferences: 'Project model choices',
      fastModels: 'Fast mode models',
    },
    primaryModelLabel: 'Primary model',
    visionModelLabel: 'Vision fallback model',
    visionInherit: 'Use inherited model: {model} (reads images)',
    visionNoFallback: 'No fallback model: images will not be processed',
    visionNoFallbackBadge: 'No fallback',
    compactModelLabel: 'Compaction model',
    projectModelsTitle: 'Per-project models',
    projectModelsCount: '{n} projects',
    projectModelsNone: 'None',
    projectModelsEmpty: 'No project has a model of its own yet.',
    projectModelsClear: 'Clear the model pinned to {project}',
    instanceModelsTitle: 'Instance models',
    openSettings: 'Open Settings',
    thinkingLabel: 'Reasoning effort',
    thinkingDefault: 'Default',
    autoCompact: 'Automatically shorten context',
    autoCompactToggle: 'Automatically shorten long conversations',
    autoCompactAt: 'Shorten when context is full',
    compactByModelTitle: 'Compaction per model',
    compactByModelGlobal: 'Default threshold',
    compactByModelGlobalHint: 'The share of the context window at which the conversation is compacted. Every model below that carries no own value uses it.',
    compactByModelOwn: 'own',
    compactByModelFilter: 'Filter models',
    compactByModelNone: 'No model matches this filter',
    fastModeTitle: 'Fast mode',
    fastModeUnavailable: 'Fast mode is not available for your models',
    fastModeRoutes: 'Fast mode works with ChatGPT sign-in, OpenAI and Azure OpenAI API keys and Anthropic API keys, for the models the catalog lists with it. Models on a Claude subscription do not support it.',
    yoloTitle: 'YOLO mode',
    yoloToggle: 'Automatically approve requests to use tools (YOLO)',
    yoloWarning: 'When enabled, requests to use tools and run commands are automatically approved in all new sessions. Rules that explicitly deny an action still apply. Use with caution.',
    yoloConfirmTitle: 'Enable YOLO mode?',
    yoloConfirm: 'Enable YOLO',
    unattendedTitle: 'Unattended runs',
    unattendedAllow: 'Allow',
    unattendedDeny: 'Block',
    permTitle: 'Command permissions',
    permToolsTitle: 'Tool rules',
    permEmpty: 'No rules yet. Commands you approve with “Always allow” in chat will appear here.',
    permPatternPlaceholder: 'e.g. git status*',
    permAdd: 'Add',
    permDeleteTitle: 'Delete this rule?',
    permAllow: 'Allow',
    permAsk: 'Ask',
    permDeny: 'Deny',
    permMore: '+{n} more',
    saveError: 'Saving the settings failed',
  },
  alerts: {
    bellLabel: 'Alerts',
    panelTitle: 'Alerts',
    markAllRead: 'Mark all read',
    markRead: 'Mark read',
    dismiss: 'Dismiss',
    emptyTitle: 'No alerts',
    emptyDescription: 'New alerts will appear here.',
    loadError: 'Could not load alerts.',
    saveError: 'Could not save the settings.',
    prefsTitle: "Notifications",
    kinds: "Notification kinds",
    kindsHint: "Choose what reaches your bell and subscribed devices. Muted kinds arrive nowhere. New kinds are enabled by default.",
    enabledCount: "{n} of {total} enabled",
    manage: "Manage notifications",
  },
  push: {
    title: "This device",
    deviceToggle: "Push notifications",
    deniedStatus: "Blocked",
    denied: "Allow notifications in your browser’s site settings, then return here.",
    unsupported: "Not supported",
    error: "Could not set up notifications.",
    homeScreen: "Add Chetty to Home Screen",
    homeScreenHint: "In Safari, choose Share → Add to Home Screen, then open Chetty from its Home Screen icon and enable notifications.",
    updateRequired: "Update iOS",
    updateRequiredHint: "Home Screen notifications require iOS or iPadOS 16.4 or newer.",
    reenable: "Turn on again",
    reenableHint: "Reconnect this device after the notification key changed.",
    impersonationHint: "Device notifications cannot be changed while impersonating another account.",
  },
  assetEditor: {
    search: 'Search…',
    emptySearch: 'Nothing matches the filter',
    filterAll: 'All',
    filterUser: 'Custom',
    filterBuiltin: 'Built-in',
    colName: 'Name',
    colSource: 'Source',
    openRow: 'Open entry: {name}',
    selectRow: 'Select entry: {name} ({scope})',
    selectedCount: '{n} selected',
    clearSelection: 'Clear selection',
    bulkEnable: 'Enable selected',
    bulkDisable: 'Disable selected',
    bulkDelete: 'Delete selected',
    bulkDeleteTitle: 'Delete selected entries?',
    bulkDeleteBody: 'Delete the selected entries ({n})? This cannot be undone.',
  },
  desktop: {
    viewerConnection: 'Screen connection',
    controlConnection: 'Control connection',
    moreActions: 'More actions',
    pictureQuality: 'Quality',
    pictureSpeed: 'Speed',
    dismissNarration: 'Dismiss agent message',
    enlarge: 'Enlarge view',
    closeView: 'Close view',
    takeControl: 'Take control',
    returnToAgent: 'Return to agent',
    controlledElsewhere: 'Controlled in another window',
    statusClosed: 'Closed',
    statusViewerLimit: 'Too many viewers',
    statusDisconnected: 'Disconnected',
    statusStalled: 'The preview is not updating',
    statusHandoverPending: 'Waiting for the current action to finish',
    statusYouControl: 'You control',
    statusUserControl: 'User control',
    statusWaitingForUser: 'Waiting for user input',
    statusAgentControl: 'Agent control',
    statusConnecting: 'Connecting',
    connecting: 'Connecting to the screen\u2026',
    unavailable: 'The live view is unavailable',
    openToView: 'Open the screen to see it live.',
    agentWaiting: 'The agent is waiting for you to return control',
    requestFailed: 'The desktop request failed.',
  },
  pluginUi: {
    eyebrow: 'Extension',
    unavailableTitle: 'Interface unavailable',
    manage: 'Manage plugins',
    pickerUnavailable: '/{command} from {plugin} cannot be displayed here.',
    notGranted: 'This extension is unavailable. It may be disabled, missing, or not granted to your account. An administrator can check the plugin settings and account grants.',
    incompatible: 'This plugin needs a newer version of the application. Update the server to use its interface.',
    loadFailed: 'The plugin interface failed to load. Check the plugin logs in Settings → Plugins.',
    pageMissing: 'The plugin has no page at this address.',
    settingsUnavailable: 'The plugin provides no settings panel for this section.',
    crashed: 'The plugin interface encountered an error. The rest of the application is unaffected.',
  },
  help: {
    embeddingDimensions: "Leave empty to use the model's default vector size.",
    embeddingProvider: 'The API key comes from the selected model provider (Settings → Models).',
    embeddingIntro: 'Turns memories into vectors so they can be searched by meaning. Applies to the entire workspace.',
    rolePrompt: 'Additional instructions added to conversations for this role.',
    projectNotes: "Context available to the assistant when working in this project.",
    projectPath: "Absolute path to the project on disk.",
    projectSlug: "Short identifier, e.g. project-name.",
    computerName: "The name this computer is shown under in the projects and the chat picker.",
    accountPlatformLinks: 'Link a chat account so the assistant recognizes your messages on that platform and gives you access to your tools and permissions. Only platforms connected to this instance are listed.',
    accountUiScale: "The interface adapts to the window width. This slider sets its size relative to normal (100% is the default) on this device.",
    memoryRecall: "After you send a message, automatically search your memory and add the most relevant memories as context for the reply.",
    memoryLiveRecall: 'The assistant can search memory again while it works, based on what it is doing: the files it opens, the tools it runs and the errors it encounters. Without this, it relies only on your opening message, which may not reflect how the work unfolds.',
    memorySave: "After the assistant replies, let it save durable new facts from the exchange to your memory in the background.",
    skillLearning: "From time to time Chetty looks at the latest exchange in the background and saves a reusable way of working as one of your personal skills, available like any skill you save yourself. Later it may improve only the skills it wrote itself; once you edit one, it stays yours. Off by default.",
    cliAutoCompact: "When the conversation nears the model’s context limit, summarize it to free up room.",
    cliPrimaryModel: "Every new conversation starts with this model. If you leave it set to inherit, it uses the instance's default model.",
    cliProjectModels: 'When you switch models in a Git project, the choice is remembered for that project and used in all later conversations there. Clear the choice to return the project to your primary model.',
    cliInstanceModels: 'Set roles for the entire instance, including the utility model, daily digest and embedding, in Settings → Models. Administrators only.',
    cliThinking: "How much effort the model puts into thinking before it answers. Higher levels mean more thorough but slower replies; Default leaves the choice to the model. It applies to the primary model above.",
    cliVisionModel: 'Used for messages with images that the primary model cannot read. If you leave it set to inherit, the primary model handles images. If it cannot read them, that is shown next to the option.',
    cliCompactModel: "The model used to summarize conversations automatically and with the /compact command. It can use a different provider than the chat model, such as a cheaper one. If left empty, the conversation's model creates the summary.",
    cliFastMode: 'Choose the models that use Fast mode, in every conversation and sub-agent. /fast turns it on or off for the model of the current conversation.',
    cliCompactByModel: 'Context size differs by model, so the same percentage represents a different amount for each. Set the compaction threshold separately for each selected model. The others use the default set above.',
    cliUnattendedAsks: 'Approval prompts appear only in interactive chat (web or CLI). No one can answer them in unattended runs, such as scheduled jobs, platform channels and sub-agents. So "ask" rules are either allowed automatically (default) or blocked (strict mode). Explicit allow/deny rules still apply. YOLO does not override strict blocking.',
    cliPermissions: "Rules for shell commands the assistant runs; * matches anything. Examples: “git status*” → Allow runs it without asking, “rm *” → Deny always blocks it. Later rules override earlier ones (last match wins) — a new rule is added to the end, and re-adding an existing pattern moves it there. A shell command that no rule matches runs without asking. Commands approved with “Always allow” in chat appear here too.",
    pushEnable: 'We will notify you when a reply is ready or something needs your attention, even if the app is closed.',
    tokenTtl: "How many days a login token stays valid before it expires (forces re-login)",
    pushContact: "The address used to sign push notifications so the push service can contact you about this instance. It must be real and reachable; Apple otherwise rejects the notification. Leave empty to use this instance's public URL.",
    pluginsManage: 'Built-in plugins are part of the app; user plugins are installed from the marketplace. Enabling, disabling or removing a plugin restarts Chetty. You can remove a built-in plugin, which hides it and stops it from loading, then restore it from Available at any time. Uninstalling a user plugin also removes its files.',
    pluginEnable: 'Enabling or disabling joins a short quiet window, then restarts Chetty. Running work is saved and resumes after the restart. Only work that cannot resume gets a bounded settle wait. Unanswered questions are lost.',
  },
} as const;
